import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
import ReactPlayer from 'react-player';
import actionQueue from '../utils/actionQueue';
import { ACTION_CODES, getActionLabel, getActionColor } from './utils/actionUtils';
import { formatTime } from './utils/timeUtils';
import { getPlayerAvatarUrl } from './utils/playerUtils';
import ShotChartOverlay, { QuickZoneSelector } from './components/ShotChartOverlay';

// MUI
import {
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  Avatar,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  TextField,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Slider,
  Tooltip,
  Chip,
  Grid,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Fab
} from '@mui/material';
import { deepOrange, blue, green } from '@mui/material/colors';

// Icons
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Forward5Icon from '@mui/icons-material/Forward5';
import Forward10Icon from '@mui/icons-material/Forward10';
import Replay5Icon from '@mui/icons-material/Replay5';
import Replay10Icon from '@mui/icons-material/Replay10';
import VideocamIcon from '@mui/icons-material/Videocam';
import TimelineIcon from '@mui/icons-material/Timeline';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/Warning';

// React DnD
import {
  DndProvider,
  useDrag,
  useDrop,
  useDragLayer
} from 'react-dnd';
import {
  MultiBackend,
  MouseTransition,
  TouchTransition
} from 'react-dnd-multi-backend';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';

// Additional Icons
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GroupIcon from '@mui/icons-material/Group';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true },
      preview: true,
      transition: TouchTransition,
    },
  ],
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const section = process.env.REACT_APP_SECTION;

// Constants
const STATS_MODE = {
  SELECTION: null,
  LIVE: 'live',
  VIDEO: 'video'
};

const ItemTypes = { PLAYER: 'PLAYER' };

// -----------------------------------------------------------------------------
// Timeline Component pour visualiser les actions
// -----------------------------------------------------------------------------
function ActionTimeline({ actions, videoDuration, currentTime, onSeek, t }) {
  const timelineRef = useRef(null);

  const handleClick = (e) => {
    if (!timelineRef.current || !videoDuration) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const seekTime = percentage * videoDuration;
    onSeek(seekTime);
  };

  return (
    <Paper
      ref={timelineRef}
      sx={{
        p: 1,
        position: 'relative',
        height: 60,
        cursor: 'pointer',
        background: 'linear-gradient(90deg, #f5f5f5 0%, #e0e0e0 100%)'
      }}
      onClick={handleClick}
    >
      {/* Current time indicator */}
      {videoDuration > 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: `${(currentTime / videoDuration) * 100}%`,
            top: 0,
            bottom: 0,
            width: 2,
            bgcolor: 'primary.main',
            zIndex: 2
          }}
        />
      )}

      {/* Action markers */}
      {actions.map((action, idx) => {
        const leftPercent = videoDuration > 0
          ? (action.videoTimestamp / videoDuration) * 100
          : 0;

        // Couleur selon le type d'action - utilise la fonction getActionColor
        const color = getActionColor(action.actionCode || action.action);

        return (
          <Tooltip key={idx} title={`${action.playerName}: ${action.action}`}>
            <Box
              sx={{
                position: 'absolute',
                left: `${leftPercent}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: color,
                border: '1px solid white',
                zIndex: 1,
                '&:hover': {
                  width: 12,
                  height: 12
                }
              }}
            />
          </Tooltip>
        );
      })}
    </Paper>
  );
}

// -----------------------------------------------------------------------------
// Custom Drag Layer
// -----------------------------------------------------------------------------
function CustomDragLayer() {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item) return null;

  const style = {
    position: 'fixed',
    pointerEvents: 'none',
    left: currentOffset.x,
    top: currentOffset.y,
    transform: 'translate(-50%, -50%)',
    zIndex: 100,
  };

  return (
    <Box style={style}>
      <Avatar
        sx={{ width: 80, height: 80 }}
        src={item.player.avatarUrl}
      >
        {item.player.nom?.charAt(0).toUpperCase() || '?'}
      </Avatar>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// PlayerListItem
// -----------------------------------------------------------------------------
function PlayerListItem({ player, onSwap }) {
  const [{ isDragging }, dragRef] = useDrag({
    type: ItemTypes.PLAYER,
    item: { player, source: 'left' },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: ItemTypes.PLAYER,
    canDrop: (dragItem) => dragItem.source === 'right',
    drop: (dragItem) => onSwap(dragItem.player, player),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  return (
    <ListItem
      ref={(node) => {
        dragRef(node);
        dropRef(node);
      }}
      sx={{
        alignItems: 'center',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        backgroundColor: isOver && canDrop ? '#ddd' : 'transparent',
      }}
    >
      <img
        src={player.avatarUrl}
        alt={`${player.nom} avatar`}
        style={{
          borderRadius: '50%',
          width: 40,
          height: 40,
          marginRight: 8
        }}
      />
      {player.nom}
    </ListItem>
  );
}

// -----------------------------------------------------------------------------
// Component Principal: EstadisticasNew
// -----------------------------------------------------------------------------
export default function EstadisticasNew({ groupeId, groupName, onNavigationAttempt }) {
  const { t } = useTranslation();

  // Mode de stats (null, 'live', 'video')
  const [statsMode, setStatsMode] = useState(STATS_MODE.SELECTION);

  // Players
  const [players, setPlayers] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);
  const [teamName, setTeamName] = useState(groupName || '');

  // Video mode states
  const [availableGames, setAvailableGames] = useState([]);
  const [gamesPagination, setGamesPagination] = useState(null);
  const [selectedVideoGame, setSelectedVideoGame] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const playerRef = useRef(null);

  // Game configuration
  const [currentGame, setCurrentGame] = useState(null);
  const [unpublishedGame, setUnpublishedGame] = useState({
    game_label: '',
    adversary_name: '',
    number_of_periods: 4,
    period_duration: 10,
    game_date: new Date().toISOString().split('T')[0],
    game_location: 'domicile',
    score: '',
    notes: ''
  });

  // Game state
  const [currentPeriod, setCurrentPeriod] = useState(1);
  const [periodSeconds, setPeriodSeconds] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [puntuacionLocal, setPuntuacionLocal] = useState(0);
  const [puntuacionVisitante, setPuntuacionVisitante] = useState(0);

  // Actions
  const [actionsLog, setActionsLog] = useState([]);
  const [lastAction, setLastAction] = useState(null);

  // Dialogs
  const [openShotDialog, setOpenShotDialog] = useState(false);
  const [activeQualifiers, setActiveQualifiers] = useState([]); // ['PAINT', 'FASTBREAK', 'SECOND_CHANCE']
  const [shotPlayer, setShotPlayer] = useState(null);
  const [shotChartOpen, setShotChartOpen] = useState(false);
  const [shotChartAction, setShotChartAction] = useState(null); // {player, actionCode, made, actionIndex}
  const [gameShotChartData, setGameShotChartData] = useState([]); // shots accumulated during this game
  const [openSpecialDialog, setOpenSpecialDialog] = useState(false);
  const [specialPlayer, setSpecialPlayer] = useState(null);
  const [openConfigGameDialog, setOpenConfigGameDialog] = useState(false);
  const [openVideoSelectionDialog, setOpenVideoSelectionDialog] = useState(false);
  const [openNewVideoDialog, setOpenNewVideoDialog] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [selectedActionIndex, setSelectedActionIndex] = useState(null);
  const [openExitConfirmDialog, setOpenExitConfirmDialog] = useState(false);

  // Track if game is in progress (configured but not saved)
  const [gameInProgress, setGameInProgress] = useState(false);

  // Store pending navigation callback
  const pendingNavigationRef = useRef(null);

  // Track internet connection and save status
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  // Period offsets for video mode
  const [periodOffsets, setPeriodOffsets] = useState([0, 0, 0, 0]);

  // Time tracking
  const [playerTimeTracking, setPlayerTimeTracking] = useState({});

  // UI optimization states
  const [headerVisible, setHeaderVisible] = useState(false);
  const [playerBenchCollapsed, setPlayerBenchCollapsed] = useState(false);
  const [videoOffset, setVideoOffset] = useState(-5); // Default -5 seconds offset for actions
  const [playbackRate, setPlaybackRate] = useState(1);

  // Timeline zoom and scroll state
  const [timelineZoom, setTimelineZoom] = useState(1);
  const [timelineScroll, setTimelineScroll] = useState(0);
  const timelineRef = useRef(null);

  // Live mode specific refs for real-time tracking
  const realTimeRef = useRef(0);
  const realTimeIntervalRef = useRef(null);
  const [periodStarted, setPeriodStarted] = useState(false); // Track if period has been started

  // Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [snackbarAction, setSnackbarAction] = useState(null);

  // Tags feature states
  const [openTagsDialog, setOpenTagsDialog] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedPlayersForTag, setSelectedPlayersForTag] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // Click handling for tags (single vs double click)
  const tagClickTimerRef = useRef(null);

  const timerRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Check for edit mode from GameCenterEstadisticas
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const gameId = urlParams.get('gameId');

    if (mode === 'editLive' && gameId) {
      const editData = localStorage.getItem('editLiveGame');
      if (editData) {
        const { gameData } = JSON.parse(editData);

        // Load the game in video mode with existing data
        setCurrentGame(gameData);
        setVideoUrl(gameData.videoUrl);
        setSelectedVideoGame({ game: gameData });
        setStatsMode(STATS_MODE.VIDEO);

        // Load existing actions for this game
        api.get(`/api/stats/actions/${gameId}`)
          .then(response => {
            if (response.data && response.data.actions) {
              const loadedActions = [];
              response.data.actions.forEach(actionData => {
                if (actionData.description) {
                  const detail = typeof actionData.description === 'object'
                    ? actionData.description
                    : JSON.parse(actionData.description);

                  if (detail.actions && Array.isArray(detail.actions)) {
                    detail.actions.forEach(act => {
                      loadedActions.push({
                        playerId: actionData.player_id,
                        playerName: actionData.player_name || '',
                        action: act.action,
                        period: act.period,
                        timeSec: act.timeSec,
                        videoTimestamp: act.videoTimestamp || act.timeSec,
                        isVideoMode: true
                      });
                    });
                  }
                }
              });
              setActionsLog(loadedActions);
            }
          })
          .catch(error => {
            console.error('Error loading existing actions:', error);
          });

        // Clear localStorage
        localStorage.removeItem('editLiveGame');
      }
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Load players and team name
  // ---------------------------------------------------------------------------
  useEffect(() => {
    api
      .get(`/api/cantera/${groupeId}`)
      .then((response) => {
        if (response.data && response.data.players) {
          const fetchedPlayers = response.data.players.map((p) => ({
            ...p,
            avatarUrl: getPlayerAvatarUrl(p.id),
          }));
          setPlayers(fetchedPlayers);
        }
        // Store team name from API
        if (response.data && response.data.group && response.data.group.libelle) {
          setTeamName(response.data.group.libelle);
        }
      })
      .catch((error) => {
        console.error('Error al obtener los jugadores:', error);
      });
  }, [groupeId]);

  // ---------------------------------------------------------------------------
  // Load tags for the group (filtered by groupe_id)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Load tags for both VIDEO and LIVE modes
    if (statsMode === STATS_MODE.VIDEO || statsMode === STATS_MODE.LIVE) {
      setLoadingTags(true);
      api
        .get(`/api/stats/tags/${groupeId}`)
        .then((response) => {
          if (response.data) {
            setAvailableTags(response.data);
          }
        })
        .catch((error) => {
          console.error('Error al obtener los tags:', error);
        })
        .finally(() => {
          setLoadingTags(false);
        });
    }
  }, [groupeId, statsMode]);

  // ---------------------------------------------------------------------------
  // Timer for live mode
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isRunning && statsMode === STATS_MODE.LIVE) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            return 0;
          }
          return prev - 1;
        });
        setPeriodSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning, statsMode]);

  // ---------------------------------------------------------------------------
  // Cleanup for live mode real-time clock and tag click timer
  // ---------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      // Clean up the real-time clock when component unmounts or mode changes
      if (realTimeIntervalRef.current) {
        clearInterval(realTimeIntervalRef.current);
        realTimeIntervalRef.current = null;
      }
      // Clean up tag click timer
      if (tagClickTimerRef.current) {
        clearTimeout(tagClickTimerRef.current);
        tagClickTimerRef.current = null;
      }
    };
  }, [statsMode]);

  // ---------------------------------------------------------------------------
  // Prevent navigation/close when game is in progress or video has unsaved actions
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasUnsavedData =
        (gameInProgress && statsMode === STATS_MODE.LIVE) ||
        (statsMode === STATS_MODE.VIDEO && actionsLog.length > 0);

      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = t('estadisticasNew.exitWarning');
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [gameInProgress, statsMode, actionsLog.length]);

  // Notify parent component and intercept all navigation attempts
  useEffect(() => {
    // Create a confirmation function that parent can call
    window.estadisticasConfirmNavigation = (callback) => {
      const hasUnsavedData =
        (gameInProgress && statsMode === STATS_MODE.LIVE) ||
        (statsMode === STATS_MODE.VIDEO && actionsLog.length > 0) ||
        isSaving; // Block if currently saving

      if (hasUnsavedData) {
        // Store the callback to execute after confirmation
        pendingNavigationRef.current = callback;
        setOpenExitConfirmDialog(true);
        return false; // Block navigation for now
      }
      return true; // Allow navigation immediately
    };

    return () => {
      delete window.estadisticasConfirmNavigation;
    };
  }, [gameInProgress, statsMode, actionsLog.length, isSaving]);

  // ---------------------------------------------------------------------------
  // Monitor internet connection
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      showSnackbar(t('estadisticasNew.connectionRestored'), 'success');
    };

    const handleOffline = () => {
      setIsOnline(false);
      showSnackbar(t('estadisticasNew.connectionLost'), 'error');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // IndexedDB: Check for unsaved game state on mount
  // ---------------------------------------------------------------------------
  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);

  useEffect(() => {
    if (!groupeId) return;
    actionQueue.getGameState(groupeId).then((saved) => {
      if (saved && saved.state && saved.state.gameInProgress) {
        setRecoveryData(saved);
        setRecoveryDialogOpen(true);
      }
    });
  }, [groupeId]);

  const handleRecoverGame = () => {
    if (!recoveryData) return;
    const s = recoveryData.state;
    setStatsMode(s.statsMode);
    setCurrentGame(s.currentGame);
    setActionsLog(s.actionsLog || []);
    setActivePlayers(s.activePlayers || []);
    setPlayerTimeTracking(s.playerTimeTracking || {});
    setCurrentPeriod(s.currentPeriod || 1);
    setPeriodSeconds(s.periodSeconds || 0);
    setPuntuacionLocal(s.puntuacionLocal || 0);
    setPuntuacionVisitante(s.puntuacionVisitante || 0);
    setVideoUrl(s.videoUrl || '');
    setPeriodOffsets(s.periodOffsets || [0, 0, 0, 0]);
    if (s.selectedVideoGame) setSelectedVideoGame(s.selectedVideoGame);
    setGameInProgress(true);
    setRecoveryDialogOpen(false);
    setRecoveryData(null);
    showSnackbar(t('estadisticasNew.gameRecovered'), 'success');
  };

  const handleDiscardRecovery = () => {
    actionQueue.clearGameState(groupeId);
    setRecoveryDialogOpen(false);
    setRecoveryData(null);
  };

  // IndexedDB: Auto-save game state on every action change (when game in progress)
  useEffect(() => {
    if (!gameInProgress || !groupeId || actionsLog.length === 0) return;
    actionQueue.saveGameState(groupeId, {
      statsMode,
      currentGame,
      actionsLog,
      activePlayers,
      playerTimeTracking,
      currentPeriod,
      periodSeconds,
      puntuacionLocal,
      puntuacionVisitante,
      videoUrl,
      periodOffsets,
      selectedVideoGame,
      gameInProgress,
    });
  }, [actionsLog, gameInProgress, currentPeriod, puntuacionLocal, puntuacionVisitante, activePlayers]);

  // ---------------------------------------------------------------------------
  // Keyboard shortcuts for video mode
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (statsMode !== STATS_MODE.VIDEO) return;

    const handleKeyDown = (e) => {
      // Don't handle if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          setVideoPlaying(prev => !prev);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skipVideo(e.shiftKey ? -1 : -5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skipVideo(e.shiftKey ? 1 : 5);
          break;
        case 'Comma': // < key - slow down
          e.preventDefault();
          setPlaybackRate(prev => Math.max(0.25, prev - 0.25));
          break;
        case 'Period': // > key - speed up
          e.preventDefault();
          setPlaybackRate(prev => Math.min(2, prev + 0.25));
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [statsMode]);

  // ---------------------------------------------------------------------------
  // Mode Selection Handlers
  // ---------------------------------------------------------------------------
  const handleSelectMode = async (mode) => {
    setStatsMode(mode);

    if (mode === STATS_MODE.VIDEO) {
      // Show video selection dialog with option to create new or select existing
      setOpenVideoSelectionDialog(true);

      // Load existing games with pagination
      try {
        const response = await api.get(
          `/api/stats/games-with-stats/${groupeId}?page=1&limit=20`
        );

        // Support both paginated and non-paginated response
        const games = response.data.data || response.data;
        setAvailableGames(games);
        if (response.data.pagination) {
          setGamesPagination(response.data.pagination);
        }
      } catch (error) {
        console.error('Error loading games:', error);
        setAvailableGames([]);
      }
    } else if (mode === STATS_MODE.LIVE) {
      setOpenConfigGameDialog(true);
    }
  };

  // ---------------------------------------------------------------------------
  // Load more games (pagination)
  // ---------------------------------------------------------------------------
  const loadMoreGames = async () => {
    if (!gamesPagination || gamesPagination.page >= gamesPagination.totalPages) return;
    try {
      const nextPage = gamesPagination.page + 1;
      const response = await api.get(
        `/api/stats/games-with-stats/${groupeId}?page=${nextPage}&limit=${gamesPagination.limit}`
      );
      const newGames = response.data.data || response.data;
      setAvailableGames(prev => [...prev, ...newGames]);
      if (response.data.pagination) {
        setGamesPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error loading more games:', error);
    }
  };

  // ---------------------------------------------------------------------------
  // Load existing actions for a game
  // ---------------------------------------------------------------------------
  const loadExistingActions = async (gameId) => {
    try {
      const response = await api.get(
        `/api/stats/actions/${gameId}`
      );

      if (response.data && response.data.actions) {
        // Parse actions and restore them to the actionsLog
        const restoredActions = response.data.actions.map(action => ({
          playerId: action.player_id,
          playerName: players.find(p => p.id === action.player_id)?.nom || `Player ${action.player_id}`,
          action: action.action,
          actionCode: action.actionCode || action.action,  // Support both old and new format
          period: action.period,
          timeSec: action.timeSec,
          videoTimestamp: action.videoTimestamp || action.timeSec,
          isVideoMode: true
        }));

        setActionsLog(restoredActions);

        // Calculate and set the current score based on actions
        let localScore = 0;
        restoredActions.forEach(action => {
          const code = action.actionCode || action.action;
          if (code === ACTION_CODES.TWO_PTS_MADE) localScore += 2;
          else if (code === ACTION_CODES.THREE_PTS_MADE) localScore += 3;
          else if (code === ACTION_CODES.FREE_THROW_MADE) localScore += 1;
        });
        setPuntuacionLocal(localScore);

        showSnackbar(t('estadisticasNew.actionsLoaded', { count: restoredActions.length }), 'info');
      }
    } catch (error) {
      console.error('Error loading existing actions:', error);
      showSnackbar(t('estadisticasNew.loadActionsError'), 'warning');
    }
  };

  // ---------------------------------------------------------------------------
  // Video game selection
  // ---------------------------------------------------------------------------
  const handleSelectVideoGame = async (game) => {
    // Check if game has video URL
    if (!game.game.url) {
      showSnackbar(t('estadisticasNew.noVideoUrl'), 'error');
      return;
    }

    setSelectedVideoGame(game);
    setVideoUrl(game.game.url);

    // Set period offsets
    const offsets = [
      game.game.periode1 || 0,
      game.game.periode2 || 0,
      game.game.periode3 || 0,
      game.game.periode4 || 0,
    ];
    setPeriodOffsets(offsets);

    // Configure game from selected video game
    setCurrentGame({
      game_label: game.game.game_label,
      adversary_name: game.game.adversary_name,
      number_of_periods: game.game.number_of_periods,
      period_duration: game.game.period_duration,
      game_date: game.game.game_date,
      game_location: game.game.game_location,
      existingGameId: game.game.id // Store existing game ID for updates
    });

    // Load existing actions for this game if it exists
    if (game.game.id) {
      await loadExistingActions(game.game.id);
    }

    // Parse existing score if available
    if (game.game.score) {
      const scoreParts = game.game.score.split('-');
      if (scoreParts.length === 2) {
        setPuntuacionLocal(parseInt(scoreParts[0]) || 0);
        setPuntuacionVisitante(parseInt(scoreParts[1]) || 0);
      }
    }

    setOpenVideoSelectionDialog(false);
  };

  // ---------------------------------------------------------------------------
  // Complete component reset function
  // ---------------------------------------------------------------------------
  const resetComponent = () => {
    // Mode and game state
    setStatsMode(STATS_MODE.SELECTION);
    setCurrentGame(null);
    setGameInProgress(false);

    // Players
    setActivePlayers([]);

    // Video states
    setSelectedVideoGame(null);
    setVideoUrl('');
    setVideoPlaying(false);
    setVideoDuration(0);
    setVideoCurrentTime(0);
    setPeriodOffsets([0, 0, 0, 0]);

    // Game state
    setCurrentPeriod(1);
    setPeriodSeconds(0);
    setTimeLeft(0);
    setIsRunning(false);
    setPuntuacionLocal(0);
    setPuntuacionVisitante(0);
    setPeriodStarted(false);

    // Actions and tracking
    setActionsLog([]);
    setLastAction(null);
    setPlayerTimeTracking({});

    // Dialogs
    setOpenShotDialog(false);
    setShotPlayer(null);
    setOpenSpecialDialog(false);
    setSpecialPlayer(null);
    setOpenConfigGameDialog(false);
    setOpenVideoSelectionDialog(false);
    setOpenNewVideoDialog(false);
    setNewVideoUrl('');
    setSelectedActionIndex(null);
    setOpenTagsDialog(false);
    setSelectedTag(null);
    setSelectedPlayersForTag([]);

    // UI states
    setHeaderVisible(false);
    setPlayerBenchCollapsed(false);
    setVideoOffset(-5);
    setTimelineZoom(1);
    setTimelineScroll(0);

    // Save states
    setIsSaving(false);
    setSaveFailed(false);

    // Clear refs
    realTimeRef.current = 0;
    if (realTimeIntervalRef.current) {
      clearInterval(realTimeIntervalRef.current);
      realTimeIntervalRef.current = null;
    }
    if (tagClickTimerRef.current) {
      clearTimeout(tagClickTimerRef.current);
      tagClickTimerRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // ---------------------------------------------------------------------------
  // Handle exit with confirmation if game in progress
  // ---------------------------------------------------------------------------
  const handleExitRequest = () => {
    if (gameInProgress && statsMode === STATS_MODE.LIVE) {
      setOpenExitConfirmDialog(true);
    } else if (statsMode === STATS_MODE.VIDEO && actionsLog.length > 0) {
      setOpenExitConfirmDialog(true);
    } else {
      resetComponent();
    }
  };

  const handleConfirmExit = () => {
    setOpenExitConfirmDialog(false);

    // If there's a pending navigation from parent (tab change or menu click), execute it
    if (pendingNavigationRef.current) {
      const callback = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      callback(); // Execute the pending navigation
    }
    // Otherwise, just return to selection (when using the back button in header)
    else {
      resetComponent();
    }
  };

  const handleCancelExit = () => {
    setOpenExitConfirmDialog(false);

    // Clear pending navigation
    pendingNavigationRef.current = null;
  };

  // ---------------------------------------------------------------------------
  // Configure live game
  // ---------------------------------------------------------------------------
  const handleConfigGame = () => {
    setCurrentGame({ ...unpublishedGame });
    setOpenConfigGameDialog(false);
    setGameInProgress(true); // Mark game as in progress

    // Reset game state
    setPuntuacionLocal(0);
    setPuntuacionVisitante(0);
    setCurrentPeriod(1);
    setTimeLeft(unpublishedGame.period_duration * 60);
    setPeriodSeconds(0);
    setActionsLog([]);
    setIsRunning(false);
    setPlayerTimeTracking({});
    setPeriodStarted(false); // Reset period started flag
    realTimeRef.current = 0; // Reset real time timer
  };

  // ---------------------------------------------------------------------------
  // Video controls
  // ---------------------------------------------------------------------------
  const handleVideoProgress = (state) => {
    setVideoCurrentTime(state.playedSeconds);

    // Auto update period based on video time
    if (statsMode === STATS_MODE.VIDEO && periodOffsets.length > 0) {
      let currentVideoPeriod = 1;
      let cumulativeTime = 0;

      for (let i = 0; i < periodOffsets.length; i++) {
        cumulativeTime += periodOffsets[i];
        if (state.playedSeconds < cumulativeTime) {
          currentVideoPeriod = i + 1;
          break;
        }
      }

      if (currentVideoPeriod !== currentPeriod) {
        setCurrentPeriod(currentVideoPeriod);
      }
    }
  };

  const handleVideoDuration = (duration) => {
    setVideoDuration(duration);
  };

  const handleVideoSeek = (time) => {
    if (playerRef.current) {
      playerRef.current.seekTo(time, 'seconds');
    }
  };

  const skipVideo = (seconds) => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(currentTime + seconds, 'seconds');
    }
  };

  // ---------------------------------------------------------------------------
  // Player management
  // ---------------------------------------------------------------------------
  const handlePlayerCheckIn = (playerId) => {
    const foundPlayer = [...players, ...activePlayers].find(p => p.id === playerId);

    if (foundPlayer) {
      console.log(`[IN] ${foundPlayer.nom} at ${formatTime(periodSeconds)}`);
    }

    setPlayerTimeTracking((prev) => {
      const data = prev[playerId] || {
        realTimeSegments: [],
        videoTimeSegments: [],
        currentlyOnFloor: false,
        realTimeIn: null,
        videoTimeIn: null,
        periodIn: null
      };

      return {
        ...prev,
        [playerId]: {
          ...data,
          currentlyOnFloor: true,
          realTimeIn: periodSeconds,
          videoTimeIn: videoCurrentTime,
          periodIn: currentPeriod
        }
      };
    });
  };

  const handlePlayerCheckOut = (playerId) => {
    const foundPlayer = [...players, ...activePlayers].find(p => p.id === playerId);

    setPlayerTimeTracking((prev) => {
      const pData = prev[playerId];
      if (!pData || !pData.currentlyOnFloor) return prev;

      const newRealSegment = {
        in: pData.realTimeIn,
        out: periodSeconds,
        periodIn: pData.periodIn,
        periodOut: currentPeriod
      };

      const newVideoSegment = {
        in: pData.videoTimeIn,
        out: videoCurrentTime,
        periodIn: pData.periodIn,
        periodOut: currentPeriod
      };

      if (foundPlayer) {
        console.log(`[OUT] ${foundPlayer.nom} at ${formatTime(periodSeconds)}`);
      }

      return {
        ...prev,
        [playerId]: {
          ...pData,
          currentlyOnFloor: false,
          realTimeSegments: [...pData.realTimeSegments, newRealSegment],
          videoTimeSegments: [...pData.videoTimeSegments, newVideoSegment],
          realTimeIn: null,
          videoTimeIn: null,
          periodIn: null
        }
      };
    });
  };

  // ---------------------------------------------------------------------------
  // Actions recording
  // ---------------------------------------------------------------------------
  const recordAction = (player, actionCode, points = 0) => {
    let timestamp;

    // Determine timestamp based on mode
    if (statsMode === STATS_MODE.VIDEO) {
      // Apply video offset for video mode (default -5 seconds)
      timestamp = Math.max(0, videoCurrentTime + videoOffset);
    } else if (statsMode === STATS_MODE.LIVE) {
      // Use real-time seconds for live mode (time since period start)
      timestamp = realTimeRef.current;
    } else {
      timestamp = periodSeconds;
    }

    // Store the translated label in the club's language
    const actionLabel = getActionLabel(actionCode, t);

    // Attach qualifiers for shot actions
    const isShotAction = ['2PTS_MADE', '2PTS_MISSED', '3PTS_MADE', '3PTS_MISSED', 'FT_MADE', 'FT_MISSED'].includes(actionCode);

    const newAction = {
      playerId: player.id,
      playerName: player.nom,
      action: actionLabel,  // Stored in club's language
      actionCode: actionCode,  // Keep code for internal logic
      period: currentPeriod,
      timeSec: Math.floor(timestamp),
      videoTimestamp: statsMode === STATS_MODE.VIDEO ? timestamp : null,
      realTimeTimestamp: statsMode === STATS_MODE.LIVE ? timestamp : null,
      isVideoMode: statsMode === STATS_MODE.VIDEO,
      isLiveMode: statsMode === STATS_MODE.LIVE,
      ...(isShotAction && activeQualifiers.length > 0 ? { qualifiers: [...activeQualifiers] } : {})
    };

    setActionsLog(prev => [...prev, newAction]);
    setLastAction(newAction);

    // Update score if points scored (only in LIVE mode, not in VIDEO mode)
    if (points > 0 && statsMode === STATS_MODE.LIVE) {
      setPuntuacionLocal(prev => prev + points);
    }

    const timeDisplay = statsMode === STATS_MODE.LIVE
      ? `${t('estadisticasNew.time')}: ${formatTime(timestamp)}`
      : `${player.nom}: ${actionLabel}`;

    showSnackbar(timeDisplay, 'success');

    // Show shot chart for shot actions in VIDEO mode (full court)
    // Skip for free throws (always from the same spot)
    const isFreeThrow = actionCode === 'FT_MADE' || actionCode === 'FT_MISSED';
    if (isShotAction && !isFreeThrow && statsMode === STATS_MODE.VIDEO) {
      setShotChartAction({
        player,
        actionCode,
        made: actionCode.includes('MADE'),
        timeSec: Math.floor(timestamp),
        period: currentPeriod,
      });
      setShotChartOpen(true);
    }
  };

  // Handle shot chart position recorded
  const handleShotChartRecorded = (shot) => {
    if (!shotChartAction) return;
    const shotEntry = {
      player_id: shotChartAction.player.id,
      action_code: shotChartAction.actionCode,
      court_x: shot.court_x,
      court_y: shot.court_y,
      made: shot.made,
      period: shotChartAction.period,
      time_sec: shotChartAction.timeSec,
      zone: shot.zone,
      qualifiers: activeQualifiers.length > 0 ? activeQualifiers : null,
    };
    setGameShotChartData(prev => [...prev, shotEntry]);
    setShotChartOpen(false);
    setShotChartAction(null);
  };

  const undoLastAction = () => {
    if (lastAction) {
      setActionsLog(prev => prev.filter(a => a !== lastAction));

      // Undo score if necessary - use actionCode for comparison
      let points = 0;
      const code = lastAction.actionCode || lastAction.action;
      if (code === ACTION_CODES.TWO_PTS_MADE) points = 2;
      else if (code === ACTION_CODES.THREE_PTS_MADE) points = 3;
      else if (code === ACTION_CODES.FREE_THROW_MADE) points = 1;

      // Only decrement score in LIVE mode, not in VIDEO mode
      if (points > 0 && statsMode === STATS_MODE.LIVE) {
        setPuntuacionLocal(prev => Math.max(0, prev - points));
      }

      showSnackbar(t('estadisticasNew.actionUndone'), 'info');
      setLastAction(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete specific action with auto-save
  // ---------------------------------------------------------------------------
  const deleteAction = async (actionIndex) => {
    if (actionIndex >= 0 && actionIndex < actionsLog.length) {
      const actionToDelete = actionsLog[actionIndex];
      const previousActionsLog = [...actionsLog];

      // Optimistic update: remove from actions log immediately
      const updatedActionsLog = actionsLog.filter((_, index) => index !== actionIndex);
      setActionsLog(updatedActionsLog);

      // Update score if necessary (only in LIVE mode, not in VIDEO mode)
      let points = 0;
      const code = actionToDelete.actionCode || actionToDelete.action;
      if (code === ACTION_CODES.TWO_PTS_MADE) points = 2;
      else if (code === ACTION_CODES.THREE_PTS_MADE) points = 3;
      else if (code === ACTION_CODES.FREE_THROW_MADE) points = 1;

      if (points > 0 && statsMode === STATS_MODE.LIVE) {
        setPuntuacionLocal(prev => Math.max(0, prev - points));
      }

      // Auto-save to backend
      const gameId = selectedVideoGame?.game?.id || currentGame?.id;

      if (gameId && actionToDelete.playerId) {
        try {
          // Get all remaining actions for this player
          const playerActions = updatedActionsLog.filter(a => a.playerId === actionToDelete.playerId);

          // If player has no more actions, we need to delete the entire record
          if (playerActions.length === 0) {
            await api.delete(
              `/api/stats/actions/${gameId}/${actionToDelete.playerId}`
            );
          } else {
            // Update with remaining actions
            await api.put(
              `/api/stats/actions/${gameId}/${actionToDelete.playerId}`,
              {
                actionIndex: -1,
                newTimestamp: 0,
                actions: playerActions.map(a => ({
                  action: a.action,
                  actionCode: a.actionCode || a.action,
                  period: a.period,
                  timeSec: a.timeSec,
                  videoTimestamp: a.videoTimestamp
                }))
              }
            );
          }

          // Provide undo callback
          const undoDelete = () => {
            setActionsLog(previousActionsLog);
            if (points > 0 && statsMode === STATS_MODE.LIVE) {
              setPuntuacionLocal(prev => prev + points);
            }
            // Re-save to backend with restored actions
            const restoredPlayerActions = previousActionsLog.filter(a => a.playerId === actionToDelete.playerId);
            api.put(
              `/api/stats/actions/${gameId}/${actionToDelete.playerId}`,
              {
                actionIndex: -1,
                newTimestamp: 0,
                actions: restoredPlayerActions.map(a => ({
                  action: a.action,
                  actionCode: a.actionCode || a.action,
                  period: a.period,
                  timeSec: a.timeSec,
                  videoTimestamp: a.videoTimestamp
                }))
              }
            ).catch(err => console.error('Error restoring action:', err));
            showSnackbar(t('estadisticasNew.actionUndone'), 'info');
          };

          showSnackbar(
            t('estadisticasNew.actionDeleted', { player: actionToDelete.playerName, action: actionToDelete.action }),
            'success',
            undoDelete
          );
        } catch (error) {
          console.error('❌ Error auto-saving after delete:', error);
          // Rollback: restore previous state
          setActionsLog(previousActionsLog);
          if (points > 0 && statsMode === STATS_MODE.LIVE) {
            setPuntuacionLocal(prev => prev + points);
          }
          showSnackbar(t('estadisticasNew.autoSaveError'), 'error');
        }
      } else {
        showSnackbar(t('estadisticasNew.actionDeletedLocally', { player: actionToDelete.playerName, action: actionToDelete.action }), 'info');
      }

      setSelectedActionIndex(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Update action timestamp with auto-save
  // ---------------------------------------------------------------------------
  const updateActionTimestamp = async (actionIndex, timeDelta) => {
    if (actionIndex >= 0 && actionIndex < actionsLog.length) {
      const action = actionsLog[actionIndex];
      const previousActionsLog = [...actionsLog];
      const newTimestamp = Math.max(0, action.videoTimestamp + timeDelta);

      // Optimistic update
      const updatedActionsLog = actionsLog.map((act, index) => {
        if (index === actionIndex) {
          return {
            ...act,
            videoTimestamp: newTimestamp,
            timeSec: Math.floor(newTimestamp)
          };
        }
        return act;
      });
      setActionsLog(updatedActionsLog);

      // Seek video to new timestamp and play
      if (playerRef.current) {
        playerRef.current.seekTo(newTimestamp, 'seconds');
        setVideoPlaying(true);
      }

      // Auto-save to backend
      const gameId = selectedVideoGame?.game?.id || currentGame?.id;

      if (gameId && action.playerId) {
        try {
          const playerActions = updatedActionsLog.filter(a => a.playerId === action.playerId);

          const originalAction = actionsLog[actionIndex];
          const playerActionIndex = playerActions.findIndex(a =>
            a.action === originalAction.action &&
            a.period === originalAction.period &&
            a.videoTimestamp === newTimestamp
          );

          const response = await api.put(
            `/api/stats/actions/${gameId}/${action.playerId}`,
            {
              actionIndex: playerActionIndex,
              newTimestamp: newTimestamp,
              actions: playerActions.map(a => ({
                action: a.action,
                actionCode: a.actionCode || a.action,
                period: a.period,
                timeSec: a.timeSec,
                videoTimestamp: a.videoTimestamp
              }))
            }
          );

          if (response.data.affectedRows > 0) {
            showSnackbar(t('estadisticasNew.autoSaved'), 'success');
          }
        } catch (error) {
          console.error('❌ Error auto-saving action:', error);
          // Rollback: restore previous state
          setActionsLog(previousActionsLog);
          showSnackbar(t('estadisticasNew.autoSaveError'), 'error');
        }
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Shot dialog handlers
  // ---------------------------------------------------------------------------
  const handlePlayerDropForShot = (player) => {
    setShotPlayer(player);
    setOpenShotDialog(true);
  };

  const handleShot = (type, result) => {
    if (!shotPlayer) return;

    let points = 0;
    let actionCode = '';

    if (type === '2pts' && result === 'made') {
      actionCode = ACTION_CODES.TWO_PTS_MADE;
      points = 2;
    } else if (type === '2pts' && result === 'missed') {
      actionCode = ACTION_CODES.TWO_PTS_MISSED;
    } else if (type === '3pts' && result === 'made') {
      actionCode = ACTION_CODES.THREE_PTS_MADE;
      points = 3;
    } else if (type === '3pts' && result === 'missed') {
      actionCode = ACTION_CODES.THREE_PTS_MISSED;
    } else if (type === 'FT' && result === 'made') {
      actionCode = ACTION_CODES.FREE_THROW_MADE;
      points = 1;
    } else if (type === 'FT' && result === 'missed') {
      actionCode = ACTION_CODES.FREE_THROW_MISSED;
    }

    recordAction(shotPlayer, actionCode, points);

    setOpenShotDialog(false);
    setShotPlayer(null);
  };

  // ---------------------------------------------------------------------------
  // Special actions
  // ---------------------------------------------------------------------------
  const handleSpecialAction = (action) => {
    if (!specialPlayer) return;
    recordAction(specialPlayer, action);
    setOpenSpecialDialog(false);
    setSpecialPlayer(null);
  };

  // ---------------------------------------------------------------------------
  // Tag actions handler
  // ---------------------------------------------------------------------------
  const handleApplyTag = () => {
    if (!selectedTag || selectedPlayersForTag.length === 0) {
      showSnackbar(t('estadisticasNew.selectTagAndPlayer'), 'warning');
      return;
    }

    // In LIVE mode, check if period has started
    if (statsMode === STATS_MODE.LIVE && !periodStarted) {
      showSnackbar(t('estadisticasNew.startPeriodFirst'), 'warning');
      return;
    }

    // Record the tag action for each selected player
    selectedPlayersForTag.forEach(playerId => {
      const player = [...activePlayers, ...players].find(p => p.id === playerId);
      if (player) {
        const tagAction = `Tag: ${selectedTag.libelle}`;

        if (statsMode === STATS_MODE.VIDEO) {
          // Tags are longer actions, so apply extra -5s offset in video mode
          const originalOffset = videoOffset;
          const tagTimestamp = Math.max(0, videoCurrentTime + originalOffset - 5);

          const newAction = {
            playerId: player.id,
            playerName: player.nom,
            action: tagAction,
            period: currentPeriod,
            timeSec: Math.floor(tagTimestamp),
            videoTimestamp: tagTimestamp,
            realTimeTimestamp: null,
            isVideoMode: true,
            isLiveMode: false
          };

          setActionsLog(prev => [...prev, newAction]);
        } else if (statsMode === STATS_MODE.LIVE) {
          // In live mode, use real time
          const currentRealTime = realTimeRef.current;

          const newAction = {
            playerId: player.id,
            playerName: player.nom,
            action: tagAction,
            period: currentPeriod,
            timeSec: currentRealTime,
            videoTimestamp: null,
            realTimeTimestamp: currentRealTime,
            isVideoMode: false,
            isLiveMode: true
          };

          setActionsLog(prev => [...prev, newAction]);
        }
      }
    });

    showSnackbar(
      t('estadisticasNew.tagApplied', { tag: selectedTag.libelle, count: selectedPlayersForTag.length }),
      'success'
    );

    // Reset states
    setSelectedTag(null);
    setSelectedPlayersForTag([]);
    setOpenTagsDialog(false);
  };

  // Handle tag click with proper single/double click detection
  const handleTagClick = (tag) => {
    // Clear any existing timer
    if (tagClickTimerRef.current) {
      clearTimeout(tagClickTimerRef.current);
      tagClickTimerRef.current = null;
      // This is a double click
      handleTagDoubleClick(tag);
    } else {
      // Set a timer for single click
      tagClickTimerRef.current = setTimeout(() => {
        // This is a single click
        handleQuickTagApply(tag);
        tagClickTimerRef.current = null;
      }, 250); // 250ms delay to detect double click
    }
  };

  // Quick tag apply for all players (single click)
  const handleQuickTagApply = (tag) => {
    // Apply tag to all players (active + bench)
    const allPlayers = [...activePlayers, ...players];

    // In LIVE mode, check if period has started
    if (statsMode === STATS_MODE.LIVE && !periodStarted) {
      showSnackbar(t('estadisticasNew.startPeriodFirst'), 'warning');
      return;
    }

    allPlayers.forEach(player => {
      const tagAction = `Tag: ${tag.libelle}`;

      if (statsMode === STATS_MODE.VIDEO) {
        // Tags are longer actions, so apply extra -5s offset in video mode
        const originalOffset = videoOffset;
        const tagTimestamp = Math.max(0, videoCurrentTime + originalOffset - 5);

        const newAction = {
          playerId: player.id,
          playerName: player.nom,
          action: tagAction,
          period: currentPeriod,
          timeSec: Math.floor(tagTimestamp),
          videoTimestamp: tagTimestamp,
          realTimeTimestamp: null,
          isVideoMode: true,
          isLiveMode: false
        };

        setActionsLog(prev => [...prev, newAction]);
      } else if (statsMode === STATS_MODE.LIVE) {
        // In live mode, use real time
        const currentRealTime = realTimeRef.current;

        const newAction = {
          playerId: player.id,
          playerName: player.nom,
          action: tagAction,
          period: currentPeriod,
          timeSec: currentRealTime,
          videoTimestamp: null,
          realTimeTimestamp: currentRealTime,
          isVideoMode: false,
          isLiveMode: true
        };

        setActionsLog(prev => [...prev, newAction]);
      }
    });

    showSnackbar(
      t('estadisticasNew.tagAppliedToTeam', { tag: tag.libelle, count: allPlayers.length }),
      'success'
    );
    setLastAction(null); // Don't track for undo since it's multiple actions
  };

  // Open selective dialog (double click)
  const handleTagDoubleClick = (tag) => {
    setSelectedTag(tag);
    // Don't pre-select any players - let user choose
    setSelectedPlayersForTag([]);
    setOpenTagsDialog(true);
  };

  const togglePlayerForTag = (playerId) => {
    setSelectedPlayersForTag(prev => {
      if (prev.includes(playerId)) {
        return prev.filter(id => id !== playerId);
      } else {
        return [...prev, playerId];
      }
    });
  };

  const selectAllPlayersForTag = () => {
    // Include both active players and bench players
    const allPlayerIds = [...activePlayers, ...players].map(p => p.id);
    if (selectedPlayersForTag.length === allPlayerIds.length) {
      setSelectedPlayersForTag([]);
    } else {
      setSelectedPlayersForTag(allPlayerIds);
    }
  };

  // ---------------------------------------------------------------------------
  // Save game and actions
  // ---------------------------------------------------------------------------
  const handleSaveGameAndActions = async () => {
    if (!currentGame) {
      showSnackbar(t('estadisticasNew.noGameConfigured'), 'error');
      return;
    }

    // Check internet connection - save locally if offline
    if (!navigator.onLine) {
      actionQueue.saveGameState(groupeId, {
        statsMode, currentGame, actionsLog, activePlayers,
        playerTimeTracking, currentPeriod, periodSeconds,
        puntuacionLocal, puntuacionVisitante, videoUrl,
        periodOffsets, selectedVideoGame, gameInProgress,
      });
      showSnackbar(t('estadisticasNew.savedLocally'), 'warning');
      setSaveFailed(true);
      return;
    }

    // Force check out all active players BEFORE saving
    // We need to manually build the final tracking state because setState is async
    const finalPlayerTimeTracking = { ...playerTimeTracking };

    activePlayers.forEach((p) => {
      const pData = finalPlayerTimeTracking[p.id];
      if (pData && pData.currentlyOnFloor) {
        // Manually create the check-out segment
        const newRealSegment = {
          in: pData.realTimeIn,
          out: periodSeconds,
          periodIn: pData.periodIn,
          periodOut: currentPeriod
        };

        const newVideoSegment = {
          in: pData.videoTimeIn,
          out: videoCurrentTime,
          periodIn: pData.periodIn,
          periodOut: currentPeriod
        };

        finalPlayerTimeTracking[p.id] = {
          ...pData,
          currentlyOnFloor: false,
          realTimeSegments: [...pData.realTimeSegments, newRealSegment],
          videoTimeSegments: [...pData.videoTimeSegments, newVideoSegment],
          realTimeIn: null,
          videoTimeIn: null,
          periodIn: null
        };

        console.log(`✅ Check-out forzado para ${p.nom}: ${newRealSegment.in}s -> ${newRealSegment.out}s (${newRealSegment.out - newRealSegment.in}s jugados)`);
      }

      // Also update the state for UI consistency
      handlePlayerCheckOut(p.id);
    });

    if (actionsLog.length === 0) {
      if (!window.confirm(t('estadisticasNew.noActionsConfirm'))) {
        return;
      }
    }

    setIsSaving(true);
    setSaveFailed(false);

    const finalScore = `${puntuacionLocal}-${puntuacionVisitante}`;

    try {
      let gameId;

      if (statsMode === STATS_MODE.VIDEO && currentGame.existingGameId) {
        // Update existing game with new actions
        gameId = currentGame.existingGameId;

        // Update score
        await api.put(
          `/api/stats/games/${gameId}`,
          { score: finalScore }
        );
      } else {
        // Create new game (either for live mode or new video)
        // For video games with pre-entered score, use that instead of live counters
        const scoreToSave = (currentGame.isNewGame && currentGame.score) ? currentGame.score : finalScore;

        const gameData = {
          groupe_id: groupeId,
          game_label: currentGame.game_label,
          adversary_name: currentGame.adversary_name,
          number_of_periods: currentGame.number_of_periods,
          period_duration: currentGame.period_duration,
          game_date: currentGame.game_date || new Date().toISOString().split('T')[0],
          game_location: currentGame.game_location,
          score: scoreToSave
        };

        // Add video URL and notes if this is a new game with video
        if (currentGame.isNewGame && videoUrl) {
          gameData.video_url = videoUrl;
        }

        // Add notes if provided
        if (currentGame.notes) {
          gameData.notes = currentGame.notes;
        }

        const gameResponse = await api.post(
          '/api/stats/games',
          gameData
        );
        gameId = gameResponse.data.gameId;
      }

      // Prepare actions for save - USE finalPlayerTimeTracking instead of playerTimeTracking
      const allPlayerIds = new Set([
        ...Object.keys(finalPlayerTimeTracking).map((pid) => Number(pid)),
        ...actionsLog.map((a) => a.playerId)
      ]);

      const finalActions = Array.from(allPlayerIds).map((pid) => {
        const tracking = finalPlayerTimeTracking[pid] || {
          realTimeSegments: [],
          videoTimeSegments: []
        };

        const playerActions = actionsLog
          .filter((act) => act.playerId === pid)
          .map((act) => ({
            action: act.action,
            actionCode: act.actionCode || act.action,
            period: act.period,
            timeSec: act.timeSec,
            videoTimestamp: act.videoTimestamp,
            realTimeTimestamp: act.realTimeTimestamp,
            isLiveMode: act.isLiveMode,
            isVideoMode: act.isVideoMode,
            ...(act.qualifiers ? { qualifiers: act.qualifiers } : {})
          }));

        // Log pour debug
        const totalSeconds = tracking.realTimeSegments.reduce((sum, seg) => sum + (seg.out - seg.in), 0);
        const minutes = (totalSeconds / 60).toFixed(1);
        console.log(`📊 Jugador ${pid}: ${tracking.realTimeSegments.length} segmentos, ${totalSeconds}s total = ${minutes} minutos`);

        return {
          playerId: pid,
          actions: playerActions,
          realTimeSegments: tracking.realTimeSegments,
          videoTimeSegments: tracking.videoTimeSegments
        };
      });

      // Save actions
      await api.post(
        '/api/stats/actions',
        {
          game_id: gameId,
          actions: finalActions
        }
      );

      // Save shot chart data if any
      if (gameShotChartData.length > 0) {
        try {
          await api.post('/api/stats/shot-chart', {
            game_id: gameId,
            shots: gameShotChartData,
          });
        } catch (shotErr) {
          console.error('Error saving shot chart:', shotErr);
        }
      }

      // Clear IndexedDB after successful save
      actionQueue.clearGameState(groupeId);

      showSnackbar(t('estadisticasNew.gameSaved'), 'success');

      // Reset after save
      setTimeout(() => {
        resetComponent();
      }, 2000);

    } catch (error) {
      console.error('Error saving game:', error);
      setIsSaving(false);
      setSaveFailed(true);

      // Check if it's a network error
      if (!navigator.onLine || error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        showSnackbar(t('estadisticasNew.connectionError'), 'error');
      } else {
        showSnackbar(t('estadisticasNew.saveError'), 'error');
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Utility functions
  // ---------------------------------------------------------------------------
  const showSnackbar = (message, severity = 'info', action = null) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarAction(action);
  };

  // ---------------------------------------------------------------------------
  // Render Mode Selection
  // ---------------------------------------------------------------------------
  const renderModeSelection = () => {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '80vh',
        gap: 4,
        pt: 4
      }}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          {t('estadisticasNew.selectStatsMode')}
        </Typography>

        <Grid container spacing={3} sx={{ maxWidth: 800 }}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                p: 4,
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 6
                }
              }}
              onClick={() => handleSelectMode(STATS_MODE.LIVE)}
            >
              <Box sx={{ textAlign: 'center' }}>
                <AccessTimeIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2 }}>
                  {t('estadisticasNew.liveStats')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('estadisticasNew.liveDescription')}
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                p: 4,
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 6
                }
              }}
              onClick={() => handleSelectMode(STATS_MODE.VIDEO)}
            >
              <Box sx={{ textAlign: 'center' }}>
                <VideocamIcon sx={{ fontSize: 80, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2 }}>
                  {t('estadisticasNew.videoStats')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('estadisticasNew.videoDescription')}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Render Video Mode
  // ---------------------------------------------------------------------------
  const renderVideoMode = () => {
    return (
      <Box sx={{ display: 'flex', height: headerVisible ? 'calc(100vh - 64px)' : '100vh', overflow: 'hidden', bgcolor: '#1a1a1a', width: '100%', maxWidth: '100%', position: 'relative' }}>
        {/* Compact Header Bar */}
        {!headerVisible && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bgcolor: 'rgba(0,0,0,0.9)',
            p: 0.5,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <IconButton size="small" onClick={handleExitRequest} sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
            <IconButton size="small" onClick={() => setHeaderVisible(true)} sx={{ color: 'white' }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="caption" sx={{ color: 'white' }}>
              {currentGame?.game_label} vs {currentGame?.adversary_name}
            </Typography>
          </Box>
        )}

        {/* Left Panel - Video Player (adjust width based on if action is selected) */}
        <Box sx={{
          flex: selectedActionIndex !== null ? '0 0 35%' : '0 0 50%',
          display: 'flex',
          flexDirection: 'column',
          p: 0.5,
          pt: headerVisible ? 1 : 5,
          transition: 'flex 0.3s ease'
        }}>
          {/* Video Player */}
          <Box sx={{
            flex: 1,
            position: 'relative',
            bgcolor: '#000',
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            {videoUrl ? (
              <ReactPlayer
                ref={playerRef}
                url={videoUrl}
                controls={false}
                playing={videoPlaying}
                playbackRate={playbackRate}
                width="100%"
                height="100%"
                onProgress={handleVideoProgress}
                onDuration={handleVideoDuration}
              />
            ) : (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'white'
              }}>
                <Typography>{t('estadisticasNew.noVideoLoaded')}</Typography>
              </Box>
            )}
          </Box>

          {/* Compact Video Controls */}
          <Box sx={{
            bgcolor: 'rgba(0,0,0,0.9)',
            p: 1,
            mt: 1,
            borderRadius: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => setVideoPlaying(!videoPlaying)} sx={{ color: 'white' }}>
                {videoPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <IconButton size="small" onClick={() => skipVideo(-10)} sx={{ color: 'white' }}>
                <Replay10Icon />
              </IconButton>
              <IconButton size="small" onClick={() => skipVideo(-5)} sx={{ color: 'white' }}>
                <Replay5Icon />
              </IconButton>
              <IconButton size="small" onClick={() => skipVideo(5)} sx={{ color: 'white' }}>
                <Forward5Icon />
              </IconButton>
              <IconButton size="small" onClick={() => skipVideo(10)} sx={{ color: 'white' }}>
                <Forward10Icon />
              </IconButton>

              <Box sx={{ flex: 1, mx: 1 }}>
                <Slider
                  size="small"
                  value={videoCurrentTime}
                  max={videoDuration}
                  onChange={(e, value) => handleVideoSeek(value)}
                  sx={{
                    color: 'primary.main',
                    '& .MuiSlider-thumb': { width: 12, height: 12 }
                  }}
                />
              </Box>

              <Typography variant="caption" sx={{ color: 'white', minWidth: '80px' }}>
                {formatTime(Math.floor(videoCurrentTime))} / {formatTime(Math.floor(videoDuration))}
              </Typography>

              {/* Playback Rate */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 1 }}>
                {[0.5, 1, 1.5, 2].map(rate => (
                  <Chip
                    key={rate}
                    label={`${rate}x`}
                    size="small"
                    variant={playbackRate === rate ? 'filled' : 'outlined'}
                    onClick={() => setPlaybackRate(rate)}
                    sx={{
                      height: '22px',
                      fontSize: '10px',
                      color: playbackRate === rate ? 'white' : 'grey.400',
                      bgcolor: playbackRate === rate ? 'primary.main' : 'transparent',
                      borderColor: 'grey.600',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: playbackRate === rate ? 'primary.dark' : 'rgba(255,255,255,0.1)' }
                    }}
                  />
                ))}
              </Box>

              {/* Offset indicator */}
              <Chip
                label={`${videoOffset > 0 ? '+' : ''}${videoOffset}s`}
                size="small"
                sx={{
                  height: '22px',
                  fontSize: '10px',
                  ml: 1,
                  color: '#ff9800',
                  borderColor: '#ff9800',
                  bgcolor: 'rgba(255, 152, 0, 0.15)',
                }}
                variant="outlined"
              />
            </Box>
          </Box>

          {/* Enhanced Timeline with zoom */}
          <Box sx={{
            height: '120px',
            bgcolor: 'rgba(0,0,0,0.7)',
            mt: 1,
            borderRadius: 1,
            position: 'relative',
            overflow: 'visible'
          }}>
            <ActionTimeline
              actions={actionsLog}
              videoDuration={videoDuration}
              currentTime={videoCurrentTime}
              onSeek={handleVideoSeek}
              t={t}
            />
          </Box>
        </Box>

        {/* Middle Panel - Compact Stats Interface (adjust width based on if action is selected) */}
        <Box sx={{
          flex: selectedActionIndex !== null ? '0 0 35%' : '0 0 48%',
          display: 'flex',
          flexDirection: 'column',
          p: 0.5,
          pr: selectedActionIndex !== null ? 0.5 : 1.5,
          pt: headerVisible ? 1 : 5,
          bgcolor: '#2a2a2a',
          transition: 'flex 0.3s ease'
        }}>
          {/* Compact Controls Bar */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            p: 0.5,
            bgcolor: 'rgba(0,0,0,0.5)',
            borderRadius: 1
          }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton size="small" onClick={undoLastAction} disabled={!lastAction} sx={{ color: 'white', p: 0.5 }}>
                <UndoIcon fontSize="small" />
              </IconButton>

              {/* Video Offset Adjustment - Inline */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'rgba(255,255,0,0.15)',
                borderRadius: 1,
                px: 0.5,
                border: '1px solid rgba(255,255,0,0.5)'
              }}>
                <Typography variant="caption" sx={{ color: 'yellow', fontWeight: 'bold', mr: 0.5, fontSize: '10px' }}>
                  {t('estadisticasNew.delay')}
                </Typography>
                <IconButton size="small" onClick={() => setVideoOffset(videoOffset - 1)} sx={{ color: 'yellow', p: 0.3 }}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography sx={{
                  color: 'yellow',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  minWidth: '35px',
                  textAlign: 'center'
                }}>
                  {videoOffset > 0 ? '+' : ''}{videoOffset}s
                </Typography>
                <IconButton size="small" onClick={() => setVideoOffset(videoOffset + 1)} sx={{ color: 'yellow', p: 0.3 }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Button
              size="small"
              variant="contained"
              color={!isOnline ? 'error' : isSaving ? 'info' : 'success'}
              onClick={handleSaveGameAndActions}
              disabled={!isOnline || isSaving}
              sx={{ fontSize: '11px', py: 0.5, px: 1 }}
            >
              {isSaving ? t('estadisticasNew.saving') : !isOnline ? t('estadisticasNew.noConnection') : t('estadisticasNew.save')}
            </Button>
          </Box>

          {/* Tags Section - Direct display */}
          {availableTags.length > 0 && (
            <Box sx={{
              p: 1,
              bgcolor: 'rgba(0,0,0,0.7)',
              borderRadius: 1,
              mb: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LocalOfferIcon sx={{ color: 'primary.main', fontSize: '16px' }} />
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '11px' }}>
                  {t('estadisticasNew.quickTags')}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                maxHeight: '80px',
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '2px' }
              }}>
                {availableTags.map(tag => (
                  <Chip
                    key={tag.id}
                    label={tag.libelle}
                    size="small"
                    onClick={() => handleTagClick(tag)}
                    sx={{
                      bgcolor: 'primary.dark',
                      color: 'white',
                      fontSize: '10px',
                      height: '22px',
                      cursor: 'pointer',
                      px: 0.5,
                      '&:hover': {
                        bgcolor: 'primary.main',
                        transform: 'scale(1.05)'
                      },
                      '&:active': {
                        transform: 'scale(0.95)'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Shot Qualifiers Toggle */}
          <Box sx={{ display: 'flex', gap: 0.5, px: 0.5, pb: 0.5, flexWrap: 'wrap' }}>
            {[
              { key: 'PAINT', label: t('estadisticasNew.paint'), color: '#ff6f00' },
              { key: 'FASTBREAK', label: t('estadisticasNew.fastbreak'), color: '#2196f3' },
              { key: 'SECOND_CHANCE', label: t('estadisticasNew.secondChance'), color: '#9c27b0' },
            ].map(({ key, label, color }) => (
              <Chip
                key={key}
                label={label}
                size="small"
                variant={activeQualifiers.includes(key) ? 'filled' : 'outlined'}
                onClick={() => {
                  setActiveQualifiers(prev =>
                    prev.includes(key) ? prev.filter(q => q !== key) : [...prev, key]
                  );
                }}
                sx={{
                  fontSize: '10px',
                  height: '22px',
                  color: activeQualifiers.includes(key) ? 'white' : color,
                  bgcolor: activeQualifiers.includes(key) ? color : 'transparent',
                  borderColor: color,
                  '&:hover': { bgcolor: activeQualifiers.includes(key) ? color : `${color}22` }
                }}
              />
            ))}
          </Box>

          {/* Compact Quick Actions Grid */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '3px' }
          }}>
            {activePlayers.map(player => (
              <Box key={player.id} sx={{
                p: 1,
                bgcolor: 'rgba(255,255,255,0.05)',
                borderRadius: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {player.nom}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        bgcolor: '#ff6f00',
                        minWidth: '44px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        height: '28px',
                        px: 1.5,
                        '&:hover': { bgcolor: '#e65100' }
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.FOUL)}
                    >
                      {t('estadisticasNew.buttonLabels.foul')}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        bgcolor: '#4caf50',
                        minWidth: '44px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        height: '28px',
                        px: 1.5,
                        '&:hover': { bgcolor: '#388e3c' }
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.FOUL_RECEIVED)}
                    >
                      {t('estadisticasNew.buttonLabels.foulReceived')}
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setActivePlayers(prev => prev.filter(p => p.id !== player.id));
                        handlePlayerCheckOut(player.id);
                        setPlayers(prev => [...prev, player]);
                      }}
                      sx={{ p: 0, color: 'grey.400', '&:hover': { color: 'error.main' } }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Primera fila: 2pts, 3pts, LF, REB */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.3, mb: 0.5 }}>
                  {/* 2pts button - Split green/red */}
                  <Box
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      height: '32px'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '50%',
                        height: '100%',
                        bgcolor: 'success.main',
                        '&:hover': { bgcolor: 'success.dark' }
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.TWO_PTS_MADE, 2)}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '50%',
                        height: '100%',
                        bgcolor: 'error.main',
                        '&:hover': { bgcolor: 'error.dark' }
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.TWO_PTS_MISSED)}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        pointerEvents: 'none'
                      }}
                    >
                      2pts
                    </Typography>
                  </Box>

                  {/* 3pts button - Split green/red */}
                  <Box
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      height: '32px'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '50%',
                        height: '100%',
                        bgcolor: 'success.main',
                        '&:hover': { bgcolor: 'success.dark' }
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.THREE_PTS_MADE, 3)}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '50%',
                        height: '100%',
                        bgcolor: 'error.main',
                        '&:hover': { bgcolor: 'error.dark' }
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.THREE_PTS_MISSED)}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        pointerEvents: 'none'
                      }}
                    >
                      3pts
                    </Typography>
                  </Box>

                  {/* LF (Lancer Franc) button - Split green/red */}
                  <Box
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      height: '32px'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '50%',
                        height: '100%',
                        bgcolor: 'success.main',
                        '&:hover': { bgcolor: 'success.dark' }
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.FREE_THROW_MADE, 1)}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '50%',
                        height: '100%',
                        bgcolor: 'error.main',
                        '&:hover': { bgcolor: 'error.dark' }
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.FREE_THROW_MISSED)}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        pointerEvents: 'none'
                      }}
                    >
                      {t('estadisticasNew.buttonLabels.freeThrow')}
                    </Typography>
                  </Box>

                  {/* REB button - Split offensive/defensive */}
                  <Box
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      height: '32px'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '50%',
                        height: '100%',
                        bgcolor: 'info.main',
                        '&:hover': { bgcolor: 'info.dark' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.OFFENSIVE_REBOUND)}
                    >
                      <Typography sx={{ color: 'white', fontSize: '7px', fontWeight: 'bold' }}>
                        {t('estadisticasNew.buttonLabels.reboundOff')}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '50%',
                        height: '100%',
                        bgcolor: 'info.light',
                        '&:hover': { bgcolor: 'info.main' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => recordAction(player, ACTION_CODES.DEFENSIVE_REBOUND)}
                    >
                      <Typography sx={{ color: 'white', fontSize: '7px', fontWeight: 'bold' }}>
                        {t('estadisticasNew.buttonLabels.reboundDef')}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: '2px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: 'white',
                        fontSize: '7px',
                        fontWeight: 'bold',
                        pointerEvents: 'none',
                        textShadow: '0 0 2px rgba(0,0,0,0.8)'
                      }}
                    >
                      {t('estadisticasNew.buttonLabels.rebound')}
                    </Typography>
                  </Box>
                </Box>

                {/* Segunda fila: AST, STL, TO, BLK */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.3 }}>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: 'warning.main',
                      minWidth: 0,
                      fontSize: '10px',
                      fontWeight: 'bold',
                      height: '32px',
                      '&:hover': { bgcolor: 'warning.dark' }
                    }}
                    onClick={() => recordAction(player, ACTION_CODES.ASSIST)}
                  >
                    {t('estadisticasNew.buttonLabels.assist')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: 'purple',
                      minWidth: 0,
                      fontSize: '10px',
                      fontWeight: 'bold',
                      height: '32px',
                      '&:hover': { bgcolor: '#6b46c1' }
                    }}
                    onClick={() => recordAction(player, ACTION_CODES.STEAL)}
                  >
                    {t('estadisticasNew.buttonLabels.steal')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: 'error.dark',
                      minWidth: 0,
                      fontSize: '10px',
                      fontWeight: 'bold',
                      height: '32px',
                      '&:hover': { bgcolor: 'error.main' }
                    }}
                    onClick={() => recordAction(player, ACTION_CODES.TURNOVER)}
                  >
                    {t('estadisticasNew.buttonLabels.turnover')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: 'success.dark',
                      minWidth: 0,
                      fontSize: '10px',
                      fontWeight: 'bold',
                      height: '32px',
                      '&:hover': { bgcolor: 'success.main' }
                    }}
                    onClick={() => recordAction(player, ACTION_CODES.BLOCK)}
                  >
                    {t('estadisticasNew.buttonLabels.block')}
                  </Button>
                </Box>

                {/* Tercera fila: CRG, C.P, DSV */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.3, mt: 0.4 }}>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ bgcolor: '#7b1fa2', minWidth: 0, fontSize: '9px', fontWeight: 'bold', height: '28px', p: 0, '&:hover': { bgcolor: '#4a148c' } }}
                    onClick={() => recordAction(player, ACTION_CODES.CHARGE_TAKEN)}
                  >
                    {t('estadisticasNew.buttonLabels.chargeTaken')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ bgcolor: '#4a148c', minWidth: 0, fontSize: '9px', fontWeight: 'bold', height: '28px', p: 0, '&:hover': { bgcolor: '#311b92' } }}
                    onClick={() => recordAction(player, ACTION_CODES.CHARGE_DRAWN)}
                  >
                    {t('estadisticasNew.buttonLabels.chargeDrawn')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ bgcolor: '#00695c', minWidth: 0, fontSize: '9px', fontWeight: 'bold', height: '28px', p: 0, '&:hover': { bgcolor: '#004d40' } }}
                    onClick={() => recordAction(player, ACTION_CODES.DEFLECTION)}
                  >
                    {t('estadisticasNew.buttonLabels.deflection')}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Collapsible Player Bench */}
          {!playerBenchCollapsed && (
            <Box sx={{
              maxHeight: '150px',
              overflowY: 'auto',
              bgcolor: 'rgba(0,0,0,0.3)',
              borderRadius: 1,
              p: 1,
              mt: 1,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '3px' }
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'grey.400' }}>{t('estadisticasNew.bench')}</Typography>
                {activePlayers.length >= 5 && (
                  <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                    {t('estadisticasNew.playersLimit')}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {players.map(player => (
                  <Chip
                    key={player.id}
                    label={player.nom}
                    size="small"
                    disabled={activePlayers.length >= 5}
                    onClick={() => {
                      if (activePlayers.length >= 5) {
                        showSnackbar(t('estadisticasNew.maxPlayersWarning'), 'warning');
                        return;
                      }
                      setActivePlayers(prev => [...prev, player]);
                      setPlayers(prev => prev.filter(p => p.id !== player.id));
                      handlePlayerCheckIn(player.id);
                    }}
                    sx={{
                      bgcolor: activePlayers.length >= 5 ? 'grey.900' : 'grey.800',
                      color: activePlayers.length >= 5 ? 'grey.600' : 'grey.300',
                      fontSize: '11px',
                      height: '22px',
                      cursor: activePlayers.length >= 5 ? 'not-allowed' : 'pointer',
                      '&:hover': { bgcolor: activePlayers.length >= 5 ? 'grey.900' : 'grey.700' }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Right Panel - Action Edit Panel (only visible when action is selected) */}
        {selectedActionIndex !== null && actionsLog[selectedActionIndex] && (
          <Box sx={{
            flex: '0 0 28%',
            display: 'flex',
            flexDirection: 'column',
            p: 1,
            pt: headerVisible ? 1 : 5,
            bgcolor: 'rgba(0,0,0,0.8)',
            borderLeft: '1px solid rgba(255,255,255,0.1)'
          }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: 1,
              p: 2
            }}>
              {/* Header with Close button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {t('estadisticasNew.editAction')}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setSelectedActionIndex(null)}
                  sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Action Info */}
              <Box sx={{ mb: 3, p: 1.5, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  {actionsLog[selectedActionIndex].playerName}
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                  {actionsLog[selectedActionIndex].action}
                </Typography>
                <Typography variant="caption" sx={{ color: 'grey.400' }}>
                  {formatTime(Math.floor(actionsLog[selectedActionIndex].videoTimestamp))}
                </Typography>
              </Box>

              {/* Time Adjustment */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ color: 'grey.400', mb: 2, display: 'block' }}>
                  {t('estadisticasNew.adjustTime')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => updateActionTimestamp(selectedActionIndex, -5)}
                    sx={{
                      minWidth: '40px',
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.3)',
                      '&:hover': { borderColor: 'white' }
                    }}
                  >
                    -5
                  </Button>
                  <IconButton
                    onClick={() => updateActionTimestamp(selectedActionIndex, -1)}
                    sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}
                  >
                    <RemoveIcon />
                  </IconButton>

                  <Box sx={{
                    px: 2,
                    py: 0.5,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    minWidth: '80px',
                    textAlign: 'center',
                    bgcolor: 'rgba(33, 150, 243, 0.1)'
                  }}>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {formatTime(Math.floor(actionsLog[selectedActionIndex].videoTimestamp))}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={() => updateActionTimestamp(selectedActionIndex, 1)}
                    sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}
                  >
                    <AddIcon />
                  </IconButton>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => updateActionTimestamp(selectedActionIndex, 5)}
                    sx={{
                      minWidth: '40px',
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.3)',
                      '&:hover': { borderColor: 'white' }
                    }}
                  >
                    +5
                  </Button>
                </Box>
              </Box>

              {/* Delete Action */}
              <Button
                fullWidth
                variant="contained"
                color="error"
                size="small"
                onClick={() => {
                  if (window.confirm(t('estadisticasNew.deleteActionConfirm'))) {
                    deleteAction(selectedActionIndex);
                  }
                }}
                sx={{ mt: 'auto' }}
              >
                {t('estadisticasNew.deleteAction')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Enhanced ActionTimeline component with zoom and better visualization
  const ActionTimeline = ({ actions, videoDuration, currentTime, onSeek }) => {
    const [localZoom, setLocalZoom] = useState(1);
    const [scrollPosition, setScrollPosition] = useState(0);
    const scrollContainerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(0);

    // Auto-scroll to keep current time in view when playing
    useEffect(() => {
      if (scrollContainerRef.current && localZoom > 1) {
        const container = scrollContainerRef.current;
        const containerWidth = container.clientWidth;
        const scrollWidth = container.scrollWidth;
        const currentPosition = (currentTime / videoDuration) * scrollWidth;
        const viewportCenter = containerWidth / 2;

        // Only auto-scroll if current position is outside visible area
        if (currentPosition < container.scrollLeft || currentPosition > container.scrollLeft + containerWidth) {
          container.scrollLeft = Math.max(0, currentPosition - viewportCenter);
        }
      }
    }, [currentTime, videoDuration, localZoom]);

    const handleTimelineClick = (e) => {
      if (isDragging) return;
      const container = e.currentTarget;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left + container.scrollLeft;
      const percentage = x / (rect.width * localZoom);
      onSeek(Math.min(Math.max(0, percentage * videoDuration), videoDuration));
    };

    const handleMouseDown = (e) => {
      if (e.shiftKey && scrollContainerRef.current) {
        setIsDragging(true);
        setDragStart(e.clientX + scrollContainerRef.current.scrollLeft);
        e.preventDefault();
      }
    };

    const handleMouseMove = (e) => {
      if (isDragging && scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = dragStart - e.clientX;
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 0.1 : -0.1;
        setLocalZoom(prev => Math.max(1, Math.min(10, prev + delta * prev)));
      }
    };

    // Group overlapping actions
    const groupedActions = [];
    const sortedActions = [...actions].sort((a, b) => a.videoTimestamp - b.videoTimestamp);

    sortedActions.forEach((action, index) => {
      const position = (action.videoTimestamp / videoDuration) * 100 * localZoom;
      const threshold = 2 * localZoom; // Minimum distance between markers in percentage

      let placed = false;
      for (let lane = 0; lane < groupedActions.length; lane++) {
        const canPlace = !groupedActions[lane].some(a =>
          Math.abs((a.videoTimestamp / videoDuration) * 100 * localZoom - position) < threshold
        );
        if (canPlace) {
          if (!groupedActions[lane]) groupedActions[lane] = [];
          groupedActions[lane].push({ ...action, originalIndex: actions.indexOf(action) });
          placed = true;
          break;
        }
      }
      if (!placed) {
        groupedActions.push([{ ...action, originalIndex: actions.indexOf(action) }]);
      }
    });

    // Early return after hooks
    if (videoDuration === 0) return null;

    return (
      <Box sx={{ position: 'relative', height: '120px', display: 'flex', flexDirection: 'column' }}>
        {/* Zoom Controls */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 0.5,
          bgcolor: 'rgba(0,0,0,0.5)',
          borderRadius: '4px 4px 0 0'
        }}>
          <IconButton
            size="small"
            onClick={() => setLocalZoom(Math.max(1, localZoom - 0.5))}
            disabled={localZoom <= 1}
            sx={{ color: 'white', p: 0.3 }}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ color: 'white', minWidth: '40px', textAlign: 'center' }}>
            {Math.round(localZoom * 100)}%
          </Typography>
          <IconButton
            size="small"
            onClick={() => setLocalZoom(Math.min(10, localZoom + 0.5))}
            disabled={localZoom >= 10}
            sx={{ color: 'white', p: 0.3 }}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '10px', ml: 1 }}>
            {localZoom > 1 && t('estadisticasNew.zoomHelp')}
          </Typography>
        </Box>

        {/* Timeline Container */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'auto',
            bgcolor: 'rgba(255,255,255,0.03)',
            cursor: isDragging ? 'grabbing' : (localZoom > 1 ? 'grab' : 'pointer'),
            '&::-webkit-scrollbar': { height: '6px' },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: '3px',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }
          }}
          onClick={handleTimelineClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          <Box sx={{
            position: 'relative',
            height: '100%',
            width: `${100 * localZoom}%`,
            minHeight: '80px'
          }}>
            {/* Time markers */}
            {Array.from({ length: Math.floor(videoDuration / 60) + 1 }, (_, i) => i * 60).map(time => (
              <Box key={time} sx={{
                position: 'absolute',
                left: `${(time / videoDuration) * 100}%`,
                top: 0,
                bottom: 0,
                borderLeft: '1px solid rgba(255,255,255,0.1)'
              }}>
                <Typography sx={{
                  position: 'absolute',
                  top: '2px',
                  left: '4px',
                  fontSize: '9px',
                  color: 'rgba(255,255,255,0.4)'
                }}>
                  {formatTime(time)}
                </Typography>
              </Box>
            ))}

            {/* Current time indicator */}
            <Box sx={{
              position: 'absolute',
              left: `${(currentTime / videoDuration) * 100}%`,
              top: 0,
              bottom: 0,
              width: '2px',
              bgcolor: 'error.main',
              zIndex: 10,
              boxShadow: '0 0 4px rgba(244, 67, 54, 0.5)'
            }}>
              <Box sx={{
                position: 'absolute',
                top: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '8px solid #f44336'
              }} />
            </Box>

            {/* Action markers in lanes */}
            {groupedActions.map((lane, laneIndex) => (
              lane.map((action) => {
                const position = (action.videoTimestamp / videoDuration) * 100;
                const color = getActionColor(action.actionCode || action.action);
                const isSelected = selectedActionIndex === action.originalIndex;

                return (
                  <Tooltip
                    key={`${laneIndex}-${action.originalIndex}`}
                    title={
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {action.playerName}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {action.action}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.300' }}>
                          {formatTime(Math.floor(action.videoTimestamp))}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        if (playerRef.current) {
                          playerRef.current.seekTo(action.videoTimestamp, 'seconds');
                        }
                        setSelectedActionIndex(action.originalIndex);
                      }}
                      sx={{
                        position: 'absolute',
                        left: `${position}%`,
                        top: `${20 + laneIndex * 20}px`,
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        zIndex: isSelected ? 9 : 5 - laneIndex,
                        '&:hover': { zIndex: 8 }
                      }}
                    >
                      <Box sx={{
                        width: isSelected ? '14px' : '10px',
                        height: isSelected ? '14px' : '10px',
                        borderRadius: '50%',
                        bgcolor: color,
                        border: isSelected ? '3px solid white' : '2px solid white',
                        boxShadow: isSelected ? '0 0 8px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.3)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          width: '14px',
                          height: '14px',
                          boxShadow: '0 0 8px rgba(0,0,0,0.5)'
                        }
                      }} />
                      {localZoom >= 2 && (
                        <Typography sx={{
                          fontSize: '8px',
                          color: 'white',
                          bgcolor: 'rgba(0,0,0,0.7)',
                          px: 0.5,
                          borderRadius: '2px',
                          mt: 0.3,
                          whiteSpace: 'nowrap',
                          textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                        }}>
                          {action.playerName.split(' ')[0]}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  // Keep the original renderVideoMode for reference
  const renderVideoModeOriginal = () => {
    return (
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {/* Left Panel - Video Player */}
        <Box sx={{ flex: '0 0 60%', display: 'flex', flexDirection: 'column', p: 2 }}>
          {/* Video Player */}
          <Paper sx={{ flex: 1, position: 'relative', bgcolor: '#000', mb: 2 }}>
            {videoUrl ? (
              <ReactPlayer
                ref={playerRef}
                url={videoUrl}
                controls={false}
                playing={videoPlaying}
                playbackRate={playbackRate}
                width="100%"
                height="100%"
                onProgress={handleVideoProgress}
                onDuration={handleVideoDuration}
              />
            ) : (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'white'
              }}>
                <Typography>{t('estadisticasNew.noVideoLoaded')}</Typography>
              </Box>
            )}
          </Paper>

          {/* Video Controls */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <IconButton onClick={() => setVideoPlaying(!videoPlaying)}>
                {videoPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <IconButton onClick={() => skipVideo(-5)}>
                <Replay5Icon />
              </IconButton>
              <IconButton onClick={() => skipVideo(5)}>
                <Forward5Icon />
              </IconButton>

              <Box sx={{ flex: 1, mx: 2 }}>
                <Slider
                  value={videoCurrentTime}
                  max={videoDuration}
                  onChange={(e, value) => handleVideoSeek(value)}
                  sx={{ width: '100%' }}
                />
              </Box>

              <Typography variant="body2">
                {formatTime(Math.floor(videoCurrentTime))} / {formatTime(Math.floor(videoDuration))}
              </Typography>
            </Box>

            {/* Game Score */}
            <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
              <Typography variant="h6">{currentGame?.game_label || 'Partido'}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {puntuacionLocal} - {puntuacionVisitante}
              </Typography>
              <Typography variant="h6">{currentGame?.adversary_name || 'Adversario'}</Typography>
            </Box>

            {/* Period Info */}
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Chip
                label={`Período ${currentPeriod}`}
                color="primary"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          </Paper>

          {/* Action Timeline */}
          <ActionTimeline
            actions={actionsLog}
            videoDuration={videoDuration}
            currentTime={videoCurrentTime}
            onSeek={handleVideoSeek}
          />
        </Box>

        {/* Right Panel - Stats Interface */}
        <Box sx={{ flex: '0 0 40%', display: 'flex', flexDirection: 'column', p: 2, bgcolor: '#f5f5f5' }}>
          {/* Actions Toolbar */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('estadisticasNew.quickActions')}</Typography>
              <Box>
                <IconButton onClick={undoLastAction} disabled={!lastAction}>
                  <UndoIcon />
                </IconButton>
                <Button
                  variant="contained"
                  color={!isOnline ? 'error' : isSaving ? 'info' : 'success'}
                  onClick={handleSaveGameAndActions}
                  disabled={!isOnline || isSaving}
                >
                  {isSaving ? t('estadisticasNew.saving') : !isOnline ? t('estadisticasNew.noConnection') : t('estadisticasNew.saveAll')}
                </Button>
              </Box>
            </Box>

            {/* Active Players */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('estadisticasNew.activePlayersLabel')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {activePlayers.map(player => (
                <Chip
                  key={player.id}
                  label={player.nom}
                  onDelete={() => {
                    setActivePlayers(prev => prev.filter(p => p.id !== player.id));
                    handlePlayerCheckOut(player.id);
                    setPlayers(prev => [...prev, player]);
                  }}
                  onClick={() => {
                    setShotPlayer(player);
                    setOpenShotDialog(true);
                  }}
                  avatar={<Avatar src={player.avatarUrl} />}
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>

            {/* Quick Action Buttons */}
            <Grid container spacing={1}>
              {activePlayers.map(player => (
                <Grid item xs={12} key={player.id}>
                  <Paper sx={{ p: 1 }}>
                    <Typography variant="subtitle2">{player.nom}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      <Button size="small" variant="outlined" color="success"
                        onClick={() => recordAction(player, ACTION_CODES.TWO_PTS_MADE, 2)}>
                        2pts ✓
                      </Button>
                      <Button size="small" variant="outlined" color="error"
                        onClick={() => recordAction(player, ACTION_CODES.TWO_PTS_MISSED)}>
                        2pts ✗
                      </Button>
                      <Button size="small" variant="outlined" color="success"
                        onClick={() => recordAction(player, ACTION_CODES.THREE_PTS_MADE, 3)}>
                        3pts ✓
                      </Button>
                      <Button size="small" variant="outlined" color="error"
                        onClick={() => recordAction(player, ACTION_CODES.THREE_PTS_MISSED)}>
                        3pts ✗
                      </Button>
                      <Button size="small" variant="outlined"
                        onClick={() => {
                          setSpecialPlayer(player);
                          setOpenSpecialDialog(true);
                        }}>
                        {t('estadisticasNew.more')}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Players List */}
          <Paper sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('estadisticasNew.playerBenchLabel')}</Typography>
            <List>
              {players.map(player => (
                <ListItem
                  key={player.id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => {
                    if (activePlayers.length >= 5) {
                      showSnackbar(t('estadisticasNew.maxActivePlayers'), 'warning');
                      return;
                    }
                    setPlayers(prev => prev.filter(p => p.id !== player.id));
                    setActivePlayers(prev => [...prev, player]);
                    handlePlayerCheckIn(player.id);
                  }}
                >
                  <Avatar src={player.avatarUrl} sx={{ mr: 2 }} />
                  <Typography>{player.nom}</Typography>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Render Live Mode - Estadísticas en Directo
  // ---------------------------------------------------------------------------
  const renderLiveMode = () => {
    // Démarrer le chrono réel pour les timestamps - tourne en continu comme une caméra
    const startRealTimeClock = () => {
      if (realTimeIntervalRef.current) {
        return; // Déjà en cours, ne rien faire
      }

      // Reset seulement au premier démarrage de la période
      if (!periodStarted) {
        realTimeRef.current = 0;
      }

      // Le timer continue TOUJOURS, même pendant les pauses du match
      realTimeIntervalRef.current = setInterval(() => {
        realTimeRef.current += 1;
      }, 1000);
    };

    // Démarrer/Reprendre une période
    const handleStartPeriod = () => {
      setIsRunning(true);

      // Démarrer le timer des actions seulement au premier lancement de la période
      if (!periodStarted) {
        setPeriodStarted(true);
        startRealTimeClock(); // Démarre le timer continu
      }

      console.log(`Período ${currentPeriod} ${periodStarted ? 'reanudado' : 'iniciado'}`);
    };

    // Pause (le timer visible s'arrête, mais le timer des actions continue)
    const handlePausePeriod = () => {
      setIsRunning(false);
      // NE PAS arrêter realTimeIntervalRef - il continue de tourner
      console.log(`Período ${currentPeriod} pausado - timer actions continue à ${realTimeRef.current}s`);
    };

    // Terminer une période - SEUL moment où le timer des actions se reset
    const handleEndPeriod = () => {
      setIsRunning(false);

      // Arrêter le timer des actions
      if (realTimeIntervalRef.current) {
        clearInterval(realTimeIntervalRef.current);
        realTimeIntervalRef.current = null;
      }

      // Passer à la période suivante si ce n'est pas la dernière
      if (currentPeriod < currentGame.number_of_periods) {
        setCurrentPeriod(prev => prev + 1);
        setTimeLeft(currentGame.period_duration * 60);
        setPeriodSeconds(0);
        setPeriodStarted(false); // Reset pour la prochaine période
        realTimeRef.current = 0; // Reset le timer pour la nouvelle période
      }
    };

    // Enregistrer une action avec le timestamp réel
    const recordLiveAction = (player, actionCode, points = 0) => {
      // Bloquer l'enregistrement si la période n'a pas été démarrée
      if (!periodStarted) {
        showSnackbar(t('estadisticasNew.startPeriodFirst'), 'warning');
        return;
      }

      // Haptic feedback pour tablettes
      if (navigator.vibrate) {
        navigator.vibrate(points > 0 ? [30, 20, 30] : 20);
      }

      const currentRealTime = realTimeRef.current;

      // Store the translated label in the club's language
      const actionLabel = getActionLabel(actionCode, t);

      const newAction = {
        playerId: player.id,
        playerName: player.nom,
        action: actionLabel,  // Stored in club's language
        actionCode: actionCode,  // Keep code for internal logic
        period: currentPeriod,
        timeSec: currentRealTime, // Temps depuis le début de la période
        videoTimestamp: null, // Sera calculé après ajout de la vidéo
        isLiveMode: true
      };

      setActionsLog(prev => [...prev, newAction]);
      setLastAction(newAction);

      // Update score if points scored (only in LIVE mode, not in VIDEO mode)
      if (points > 0 && statsMode === STATS_MODE.LIVE) {
        setPuntuacionLocal(prev => prev + points);
      }

      showSnackbar(`${player.nom}: ${actionLabel} (${formatTime(currentRealTime)})`, 'success');
    };

    return (
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: '#2a2a2a' }}>

        {/* Left Panel - Game Info & Timer */}
        <Box sx={{
          flex: '0 0 22%',
          display: 'flex',
          flexDirection: 'column',
          p: 0.5,
          bgcolor: '#1a1a1a',
          overflow: 'auto'
        }}>
          {/* Compact Game Header */}
          <Box sx={{ bgcolor: '#1976d2', p: 1, borderRadius: 1, mb: 0.5, position: 'relative' }}>
            <IconButton
              size="small"
              onClick={handleExitRequest}
              sx={{
                position: 'absolute',
                left: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                p: 0.5
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '13px' }}>
              {currentGame?.game_label || 'Partido'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'white', textAlign: 'center', display: 'block', fontSize: '11px' }}>
              vs {currentGame?.adversary_name}
            </Typography>
          </Box>

          {/* Score avec contrôles */}
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 1, borderRadius: 1, mb: 0.5 }}>
            {/* Mi Equipo */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#1976d2', fontSize: '9px', display: 'block', mb: 0.3, fontWeight: 'bold', textTransform: 'uppercase' }}>
                {teamName || 'Mi Equipo'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setPuntuacionLocal(prev => Math.max(0, prev - 1))}
                  disabled={statsMode === STATS_MODE.VIDEO}
                  sx={{ bgcolor: 'rgba(244, 67, 54, 0.2)', color: 'error.main', p: 0.5, '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.3)' } }}
                >
                  <RemoveIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Box sx={{ flex: 1, textAlign: 'center', bgcolor: 'rgba(25, 118, 210, 0.2)', borderRadius: 1, py: 0.5 }}>
                  <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '36px' }}>
                    {puntuacionLocal}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setPuntuacionLocal(prev => prev + 1)}
                  disabled={statsMode === STATS_MODE.VIDEO}
                  sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', color: 'success.main', p: 0.5, '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.3)' } }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Adversario */}
            <Box>
              <Typography variant="caption" sx={{ color: '#dc004e', fontSize: '9px', display: 'block', mb: 0.3, fontWeight: 'bold', textTransform: 'uppercase' }}>
                {currentGame?.adversary_name || 'Adversario'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setPuntuacionVisitante(prev => Math.max(0, prev - 1))}
                  disabled={statsMode === STATS_MODE.VIDEO}
                  sx={{ bgcolor: 'rgba(244, 67, 54, 0.2)', color: 'error.main', p: 0.5, '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.3)' } }}
                >
                  <RemoveIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Box sx={{ flex: 1, textAlign: 'center', bgcolor: 'rgba(220, 0, 78, 0.2)', borderRadius: 1, py: 0.5 }}>
                  <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#dc004e', fontSize: '36px' }}>
                    {puntuacionVisitante}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setPuntuacionVisitante(prev => prev + 1)}
                  disabled={statsMode === STATS_MODE.VIDEO}
                  sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', color: 'success.main', p: 0.5, '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.3)' } }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              {/* Opponent score buttons for +/- tracking */}
              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 0.5 }}>
                {[
                  { code: ACTION_CODES.OPP_SCORE_FT, label: 'TL', pts: 1 },
                  { code: ACTION_CODES.OPP_SCORE_2, label: '2', pts: 2 },
                  { code: ACTION_CODES.OPP_SCORE_3, label: '3', pts: 3 },
                ].map(({ code, label, pts }) => (
                  <Button
                    key={code}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setPuntuacionVisitante(prev => prev + pts);
                      // Record opponent scoring action for +/- calculation
                      const timestamp = statsMode === STATS_MODE.LIVE
                        ? realTimeRef.current
                        : Math.max(0, videoCurrentTime + videoOffset);
                      setActionsLog(prev => [...prev, {
                        playerId: 0,
                        playerName: t('estadisticasNew.opponentScore'),
                        action: getActionLabel(code, t),
                        actionCode: code,
                        period: currentPeriod,
                        timeSec: Math.floor(timestamp),
                        videoTimestamp: statsMode === STATS_MODE.VIDEO ? timestamp : undefined,
                        realTimeTimestamp: statsMode === STATS_MODE.LIVE ? timestamp : undefined,
                        isLiveMode: statsMode === STATS_MODE.LIVE,
                        isVideoMode: statsMode === STATS_MODE.VIDEO,
                      }]);
                    }}
                    sx={{
                      minWidth: 0, px: 1, py: 0.3,
                      fontSize: '10px', color: '#dc004e',
                      borderColor: 'rgba(220, 0, 78, 0.3)',
                      '&:hover': { borderColor: '#dc004e', bgcolor: 'rgba(220, 0, 78, 0.1)' }
                    }}
                  >
                    +{label}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Timer */}
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 1, borderRadius: 1, mb: 0.5 }}>
            <Chip
              icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
              label={`P${currentPeriod}/${currentGame?.number_of_periods || 4}`}
              size="small"
              color="primary"
              sx={{ fontSize: '10px', height: '20px', mb: 0.5, width: '100%' }}
            />
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: timeLeft < 60 ? 'error.main' : 'white', textAlign: 'center', fontSize: '42px' }}>
              {formatTime(timeLeft)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              {!isRunning ? (
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
                  onClick={handleStartPeriod}
                  sx={{ fontSize: '11px', py: 0.5 }}
                >
                  {periodStarted ? t('estadisticasNew.resume') : t('estadisticasNew.start')}
                </Button>
              ) : (
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<PauseIcon sx={{ fontSize: 14 }} />}
                  onClick={handlePausePeriod}
                  sx={{ fontSize: '11px', py: 0.5 }}
                >
                  {t('estadisticasNew.pause')}
                </Button>
              )}
              {periodStarted && currentPeriod < (currentGame?.number_of_periods || 4) && (
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  onClick={handleEndPeriod}
                  sx={{ fontSize: '10px', py: 0.3, color: 'white', borderColor: 'white' }}
                >
                  {t('estadisticasNew.endPeriod', { period: currentPeriod })}
                </Button>
              )}
            </Box>
          </Box>

          {/* Compact Banco */}
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 0.5, borderRadius: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ color: 'grey.400', mb: 0.5, fontSize: '10px' }}>
              Banco ({players.length})
            </Typography>
            <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '2px' } }}>
              {players.map(player => (
                <Chip
                  key={player.id}
                  label={player.nom}
                  size="small"
                  disabled={activePlayers.length >= 5}
                  onClick={() => {
                    if (activePlayers.length >= 5) {
                      showSnackbar(t('estadisticasNew.maxPlayers'), 'warning');
                      return;
                    }
                    setActivePlayers(prev => [...prev, player]);
                    setPlayers(prev => prev.filter(p => p.id !== player.id));
                    handlePlayerCheckIn(player.id);
                  }}
                  sx={{
                    bgcolor: activePlayers.length >= 5 ? 'rgba(255,255,255,0.05)' : 'rgba(33, 150, 243, 0.2)',
                    color: activePlayers.length >= 5 ? 'grey.600' : 'primary.light',
                    fontSize: '11px',
                    height: '32px',
                    mb: 0.5,
                    width: '100%',
                    cursor: activePlayers.length >= 5 ? 'not-allowed' : 'pointer',
                    '&:hover': {
                      bgcolor: activePlayers.length >= 5 ? 'rgba(255,255,255,0.05)' : 'rgba(33, 150, 243, 0.3)'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Save button */}
          <Button
            fullWidth
            variant="contained"
            color={!isOnline ? 'error' : isSaving ? 'info' : 'success'}
            startIcon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
            onClick={handleSaveGameAndActions}
            disabled={!isOnline || isSaving}
            sx={{ mt: 0.5, fontSize: '13px', py: 1.5, fontWeight: 'bold' }}
          >
            {isSaving ? t('estadisticasNew.saving') : !isOnline ? t('estadisticasNew.noConnection') : t('estadisticasNew.saveStats')}
          </Button>
        </Box>

        {/* Middle Panel - Actions Interface */}
        <Box sx={{
          flex: '0 0 76%',
          display: 'flex',
          flexDirection: 'column',
          p: 0.5,
          overflow: 'auto'
        }}>
          {/* Top Bar - Undo + Active Players */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, p: 0.5, bgcolor: 'rgba(0,0,0,0.5)', borderRadius: 1 }}>
            <IconButton size="small" onClick={undoLastAction} disabled={!lastAction} sx={{ color: 'white', p: 0.5 }}>
              <UndoIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ color: 'white', fontSize: '11px' }}>
              {t('estadisticasNew.activePlayers', { count: activePlayers.length })}
            </Typography>
          </Box>

          {/* Tags Section for Live Mode - Same as Video Mode */}
          {availableTags.length > 0 && (
            <Box sx={{
              p: 1,
              bgcolor: 'rgba(0,0,0,0.7)',
              borderRadius: 1,
              mb: 0.5
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LocalOfferIcon sx={{ color: 'primary.main', fontSize: '16px' }} />
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '11px' }}>
                  {t('estadisticasNew.quickTags')}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                maxHeight: '80px',
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '2px' }
              }}>
                {availableTags.map(tag => (
                  <Chip
                    key={tag.id}
                    label={tag.libelle}
                    size="small"
                    onClick={() => handleTagClick(tag)}
                    sx={{
                      bgcolor: 'primary.dark',
                      color: 'white',
                      fontSize: '10px',
                      height: '22px',
                      cursor: 'pointer',
                      px: 0.5,
                      '&:hover': {
                        bgcolor: 'primary.main',
                        transform: 'scale(1.05)'
                      },
                      '&:active': {
                        transform: 'scale(0.95)'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Compact Active Players with Actions - Split buttons like video mode */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '3px' }
          }}>
            {activePlayers.map(player => (
              <Box key={player.id} sx={{
                p: 0.8,
                bgcolor: 'rgba(255,255,255,0.05)',
                borderRadius: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>
                    {player.nom}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setActivePlayers(prev => prev.filter(p => p.id !== player.id));
                      handlePlayerCheckOut(player.id);
                      setPlayers(prev => [...prev, player]);
                    }}
                    sx={{ p: 0, color: 'grey.400', '&:hover': { color: 'error.main' } }}
                  >
                    <CancelIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>

                {/* Primera fila: 2pts, 3pts, FT, REB - Split buttons */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.4, mb: 0.5 }}>
                  {/* 2pts Split */}
                  <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '4px', cursor: 'pointer', height: '48px' }}>
                    <Box
                      sx={{ position: 'absolute', top: 0, left: 0, width: '50%', height: '100%', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                      onClick={() => recordLiveAction(player, ACTION_CODES.TWO_PTS_MADE, 2)}
                    />
                    <Box
                      sx={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
                      onClick={() => recordLiveAction(player, ACTION_CODES.TWO_PTS_MISSED)}
                    />
                    <Typography sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', fontSize: '11px', fontWeight: 'bold', pointerEvents: 'none' }}>
                      {t('estadisticasNew.buttonLabels.twoPoints')}
                    </Typography>
                  </Box>

                  {/* 3pts Split */}
                  <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '4px', cursor: 'pointer', height: '48px' }}>
                    <Box
                      sx={{ position: 'absolute', top: 0, left: 0, width: '50%', height: '100%', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                      onClick={() => recordLiveAction(player, ACTION_CODES.THREE_PTS_MADE, 3)}
                    />
                    <Box
                      sx={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
                      onClick={() => recordLiveAction(player, ACTION_CODES.THREE_PTS_MISSED)}
                    />
                    <Typography sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', fontSize: '11px', fontWeight: 'bold', pointerEvents: 'none' }}>
                      {t('estadisticasNew.buttonLabels.threePoints')}
                    </Typography>
                  </Box>

                  {/* FT Split */}
                  <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '4px', cursor: 'pointer', height: '48px' }}>
                    <Box
                      sx={{ position: 'absolute', top: 0, left: 0, width: '50%', height: '100%', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                      onClick={() => recordLiveAction(player, ACTION_CODES.FREE_THROW_MADE, 1)}
                    />
                    <Box
                      sx={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
                      onClick={() => recordLiveAction(player, ACTION_CODES.FREE_THROW_MISSED)}
                    />
                    <Typography sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', fontSize: '11px', fontWeight: 'bold', pointerEvents: 'none' }}>
                      {t('estadisticasNew.buttonLabels.freeThrow')}
                    </Typography>
                  </Box>

                  {/* REB Split */}
                  <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '4px', cursor: 'pointer', height: '48px' }}>
                    <Box
                      sx={{ position: 'absolute', top: 0, left: 0, width: '50%', height: '100%', bgcolor: 'info.main', '&:hover': { bgcolor: 'info.dark' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => recordLiveAction(player, ACTION_CODES.OFFENSIVE_REBOUND)}
                    >
                      <Typography sx={{ color: 'white', fontSize: '9px', fontWeight: 'bold' }}>{t('estadisticasNew.buttonLabels.reboundOff')}</Typography>
                    </Box>
                    <Box
                      sx={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', bgcolor: 'info.light', '&:hover': { bgcolor: 'info.main' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => recordLiveAction(player, ACTION_CODES.DEFENSIVE_REBOUND)}
                    >
                      <Typography sx={{ color: 'white', fontSize: '9px', fontWeight: 'bold' }}>{t('estadisticasNew.buttonLabels.reboundDef')}</Typography>
                    </Box>
                    <Typography sx={{ position: 'absolute', top: '4px', left: '50%', transform: 'translateX(-50%)', color: 'white', fontSize: '9px', fontWeight: 'bold', pointerEvents: 'none', textShadow: '0 0 2px rgba(0,0,0,0.8)' }}>
                      {t('estadisticasNew.buttonLabels.rebound')}</Typography>
                  </Box>
                </Box>

                {/* Segunda fila: AST, STL, TO, BLK, FOUL, FOUL_RCV */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.4 }}>
                  <Button size="small" variant="contained" sx={{ bgcolor: 'warning.main', minWidth: 0, fontSize: '10px', fontWeight: 'bold', height: '48px', p: 0, '&:hover': { bgcolor: 'warning.dark' } }}
                    onClick={() => recordLiveAction(player, ACTION_CODES.ASSIST)}>{t('estadisticasNew.buttonLabels.assist')}</Button>
                  <Button size="small" variant="contained" sx={{ bgcolor: 'purple', minWidth: 0, fontSize: '10px', fontWeight: 'bold', height: '48px', p: 0, '&:hover': { bgcolor: '#6b46c1' } }}
                    onClick={() => recordLiveAction(player, ACTION_CODES.STEAL)}>{t('estadisticasNew.buttonLabels.steal')}</Button>
                  <Button size="small" variant="contained" sx={{ bgcolor: 'error.dark', minWidth: 0, fontSize: '10px', fontWeight: 'bold', height: '48px', p: 0, '&:hover': { bgcolor: 'error.main' } }}
                    onClick={() => recordLiveAction(player, ACTION_CODES.TURNOVER)}>{t('estadisticasNew.buttonLabels.turnover')}</Button>
                  <Button size="small" variant="contained" sx={{ bgcolor: 'success.dark', minWidth: 0, fontSize: '10px', fontWeight: 'bold', height: '48px', p: 0, '&:hover': { bgcolor: 'success.main' } }}
                    onClick={() => recordLiveAction(player, ACTION_CODES.BLOCK)}>{t('estadisticasNew.buttonLabels.block')}</Button>
                  <Button size="small" variant="contained" sx={{ bgcolor: '#ff6f00', minWidth: 0, fontSize: '10px', fontWeight: 'bold', height: '48px', p: 0, '&:hover': { bgcolor: '#e65100' } }}
                    onClick={() => recordLiveAction(player, ACTION_CODES.FOUL)}>{t('estadisticasNew.buttonLabels.foul')}</Button>
                  <Button size="small" variant="contained" sx={{ bgcolor: '#4caf50', minWidth: 0, fontSize: '9px', fontWeight: 'bold', height: '48px', p: 0, '&:hover': { bgcolor: '#388e3c' } }}
                    onClick={() => recordLiveAction(player, ACTION_CODES.FOUL_RECEIVED)}>{t('estadisticasNew.buttonLabels.foulReceived')}</Button>
                </Box>

                {/* Tercera fila: CRG, C.P, DFL */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.4, mt: 0.4 }}>
                  <Button size="small" variant="contained" sx={{ bgcolor: '#7b1fa2', minWidth: 0, fontSize: '9px', fontWeight: 'bold', height: '36px', p: 0, '&:hover': { bgcolor: '#4a148c' } }}
                    onClick={() => recordLiveAction(player, ACTION_CODES.CHARGE_TAKEN)}>{t('estadisticasNew.buttonLabels.chargeTaken')}</Button>
                  <Button size="small" variant="contained" sx={{ bgcolor: '#4a148c', minWidth: 0, fontSize: '9px', fontWeight: 'bold', height: '36px', p: 0, '&:hover': { bgcolor: '#311b92' } }}
                    onClick={() => recordLiveAction(player, ACTION_CODES.CHARGE_DRAWN)}>{t('estadisticasNew.buttonLabels.chargeDrawn')}</Button>
                  <Button size="small" variant="contained" sx={{ bgcolor: '#00695c', minWidth: 0, fontSize: '9px', fontWeight: 'bold', height: '36px', p: 0, '&:hover': { bgcolor: '#004d40' } }}
                    onClick={() => recordLiveAction(player, ACTION_CODES.DEFLECTION)}>{t('estadisticasNew.buttonLabels.deflection')}</Button>
                </Box>
              </Box>
            ))}

            {activePlayers.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'grey.500' }}>
                <Typography variant="body2">{t('estadisticasNew.selectPlayersFromBench')}</Typography>
              </Box>
            )}
          </Box>

          {/* Log de Acciones Compact */}
          <Box sx={{ maxHeight: '120px', overflowY: 'auto', bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 1, p: 0.5, mt: 0.5,
            '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '2px' } }}>
            <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '10px', mb: 0.3, display: 'block' }}>
              {t('estadisticasNew.actions')} ({actionsLog.length})
            </Typography>
            {actionsLog.slice(-8).reverse().map((action, idx) => (
              <Box key={actionsLog.length - idx - 1} sx={{ mb: 0.2 }}>
                <Typography variant="caption" sx={{ fontSize: '9px', color: 'white' }}>
                  <strong>{action.playerName}</strong> - {action.action}
                  <span style={{ color: '#999', marginLeft: '4px' }}>
                    (P{action.period} {formatTime(action.timeSec)})
                  </span>
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Main Render
  // ---------------------------------------------------------------------------
  return (
    <DndProvider backend={MultiBackend} options={HTML5toTouch}>
      {statsMode === STATS_MODE.SELECTION && renderModeSelection()}
      {statsMode === STATS_MODE.VIDEO && renderVideoMode()}
      {statsMode === STATS_MODE.LIVE && renderLiveMode()}

      <CustomDragLayer />

      {/* Connectivity Status Bar - visible when game is in progress and offline */}
      {gameInProgress && !isOnline && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          bgcolor: '#d32f2f',
          color: 'white',
          py: 0.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          fontSize: '0.85rem',
          fontWeight: 600,
          boxShadow: '0 2px 8px rgba(211,47,47,0.4)'
        }}>
          <Box component="span" sx={{ fontSize: '1.1rem' }}>&#9888;</Box>
          {t('estadisticasNew.offlineBar')}
          <Box component="span" sx={{ fontWeight: 400, ml: 1, fontSize: '0.8rem' }}>
            ({t('estadisticasNew.offlineBarSub')})
          </Box>
        </Box>
      )}

      {/* Shot Chart Overlay Dialog (VIDEO mode) */}
      <Dialog
        open={shotChartOpen}
        onClose={() => { setShotChartOpen(false); setShotChartAction(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none' } }}
      >
        <ShotChartOverlay
          onShotRecorded={handleShotChartRecorded}
          onClose={() => { setShotChartOpen(false); setShotChartAction(null); }}
          playerName={shotChartAction?.player?.nom || ''}
          actionCode={shotChartAction?.actionCode || ''}
          made={shotChartAction?.made || false}
          existingShots={gameShotChartData.filter(s =>
            shotChartAction && s.player_id === shotChartAction.player.id
          )}
        />
      </Dialog>

      {/* Recovery Dialog */}
      <Dialog open={recoveryDialogOpen} onClose={handleDiscardRecovery}>
        <DialogTitle>{t('estadisticasNew.recoveryTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('estadisticasNew.recoveryMessage', {
              actions: recoveryData?.state?.actionsLog?.length || 0,
              minutes: recoveryData ? Math.round((Date.now() - recoveryData.timestamp) / 60000) : 0
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardRecovery} color="error">
            {t('estadisticasNew.recoveryDiscard')}
          </Button>
          <Button onClick={handleRecoverGame} variant="contained" color="primary">
            {t('estadisticasNew.recoveryRestore')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={snackbarAction ? 8000 : 3000}
        onClose={() => { setSnackbarMessage(''); setSnackbarAction(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSnackbarMessage(''); setSnackbarAction(null); }}
          severity={snackbarSeverity}
          action={snackbarAction && (
            <Button color="inherit" size="small" onClick={() => { snackbarAction(); setSnackbarMessage(''); setSnackbarAction(null); }}>
              {t('estadisticasNew.undo')}
            </Button>
          )}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Video Selection Dialog */}
      <Dialog
        open={openVideoSelectionDialog}
        onClose={() => setOpenVideoSelectionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('estadisticasNew.videoStatsDialog')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('estadisticasNew.createNewGameWithVideo')}
            </Typography>
            <Button
              variant="contained"
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
              size="large"
              startIcon={<VideocamIcon />}
              onClick={() => {
                setOpenVideoSelectionDialog(false);
                setOpenNewVideoDialog(true);
              }}
            >
              {t('estadisticasNew.provideVideoUrl')}
            </Button>
          </Box>

          {availableGames.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('estadisticasNew.selectExistingGame')}
              </Typography>
              <List>
                {availableGames.filter(g => g.game).map((game) => {
                  const hasVideo = !!game.game.url;
                  return (
                    <ListItem
                      key={game.game.id}
                      button
                      disabled={!hasVideo}
                      onClick={() => handleSelectVideoGame(game)}
                      sx={{
                        border: hasVideo ? '1px solid #ddd' : '1px solid #f0f0f0',
                        borderRadius: 1,
                        mb: 1,
                        opacity: hasVideo ? 1 : 0.5,
                        cursor: hasVideo ? 'pointer' : 'not-allowed',
                        '&:hover': { bgcolor: hasVideo ? 'action.hover' : 'transparent' }
                      }}
                    >
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="h6" color={hasVideo ? 'text.primary' : 'text.disabled'}>
                            {game.game.game_label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            vs {game.game.adversary_name} - {new Date(game.game.game_date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('estadisticasNew.score')}: {game.game.score || t('estadisticasNew.noScore')}
                          </Typography>
                          {!hasVideo && (
                            <Typography variant="caption" color="error" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                              {t('estadisticasNew.addVideoUrlFromGameCenter')}
                            </Typography>
                          )}
                        </Box>
                        {hasVideo ? (
                          <Chip label={t('estadisticasNew.withVideo')} color="success" size="small" icon={<VideocamIcon />} />
                        ) : (
                          <Chip label={t('estadisticasNew.withoutVideo')} color="error" size="small" icon={<WarningIcon />} />
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
              {gamesPagination && gamesPagination.page < gamesPagination.totalPages && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button variant="outlined" onClick={loadMoreGames}>
                    {t('estadisticasNew.loadMore')} ({gamesPagination.total - availableGames.length} {t('estadisticasNew.remaining')})
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenVideoSelectionDialog(false);
            resetComponent();
          }}>
            {t('estadisticasNew.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shot Dialog */}
      <Dialog open={openShotDialog} onClose={() => setOpenShotDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('estadisticasNew.shotSelection')}</DialogTitle>
        <DialogContent>
          {shotPlayer && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Avatar
                src={shotPlayer.avatarUrl}
                sx={{ width: 80, height: 80, margin: '0 auto', mb: 1 }}
              />
              <Typography variant="h6">{shotPlayer.nom}</Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                onClick={() => handleShot('2pts', 'made')}
              >
                {t('estadisticasNew.twoPointsScored')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                size="large"
                onClick={() => handleShot('2pts', 'missed')}
              >
                {t('estadisticasNew.twoPointsMissed')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                onClick={() => handleShot('3pts', 'made')}
              >
                {t('estadisticasNew.threePointsScored')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                size="large"
                onClick={() => handleShot('3pts', 'missed')}
              >
                {t('estadisticasNew.threePointsMissed')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                onClick={() => handleShot('FT', 'made')}
              >
                {t('estadisticasNew.freeThrowScored')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                size="large"
                onClick={() => handleShot('FT', 'missed')}
              >
                {t('estadisticasNew.freeThrowMissed')}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenShotDialog(false)}>{t('estadisticasNew.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Special Actions Dialog - Only less common actions */}
      <Dialog open={openSpecialDialog} onClose={() => setOpenSpecialDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('estadisticasNew.otherActions')}</DialogTitle>
        <DialogContent>
          {specialPlayer && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="subtitle1">{specialPlayer.nom}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              sx={{ bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
              onClick={() => handleSpecialAction(ACTION_CODES.TURNOVER)}
            >
              {t('estadisticasNew.turnover')}
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ bgcolor: 'success.dark', '&:hover': { bgcolor: 'success.main' } }}
              onClick={() => handleSpecialAction(ACTION_CODES.BLOCK)}
            >
              {t('estadisticasNew.block')}
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ bgcolor: 'warning.dark', '&:hover': { bgcolor: 'warning.main' } }}
              onClick={() => handleSpecialAction(ACTION_CODES.FOUL)}
            >
              {t('estadisticasNew.foul')}
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ bgcolor: 'info.dark', '&:hover': { bgcolor: 'info.main' } }}
              onClick={() => handleSpecialAction(ACTION_CODES.SCREEN)}
            >
              {t('estadisticasNew.screen')}
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ bgcolor: 'grey.600', '&:hover': { bgcolor: 'grey.700' } }}
              onClick={() => handleSpecialAction(ACTION_CODES.TIMEOUT)}
            >
              {t('estadisticasNew.timeout')}
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}
              onClick={() => handleSpecialAction(ACTION_CODES.TECHNICAL)}
            >
              {t('estadisticasNew.technical')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setOpenSpecialDialog(false)}>{t('estadisticasNew.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* New Video Dialog - Configure game with video URL */}
      <Dialog open={openNewVideoDialog} onClose={() => setOpenNewVideoDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('estadisticasNew.createGameWithVideo')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('estadisticasNew.provideVideoAndInfo')}
          </Typography>

          <TextField
            margin="dense"
            label={t('estadisticasNew.videoUrl')}
            fullWidth
            value={newVideoUrl}
            onChange={(e) => setNewVideoUrl(e.target.value)}
            placeholder={t('estadisticasNew.videoUrlPlaceholder')}
            sx={{ mb: 2 }}
            helperText={t('estadisticasNew.videoUrlHelper')}
          />

          {newVideoUrl && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption">{t('estadisticasNew.videoPreview')}</Typography>
              <ReactPlayer
                url={newVideoUrl}
                width="100%"
                height="200px"
                controls={false}
              />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <TextField
            margin="dense"
            label={t('estadisticasNew.gameName')}
            fullWidth
            value={unpublishedGame.game_label}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, game_label: e.target.value })}
            sx={{ mb: 1 }}
          />
          <TextField
            margin="dense"
            label={t('estadisticasNew.adversaryName')}
            fullWidth
            value={unpublishedGame.adversary_name}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, adversary_name: e.target.value })}
            sx={{ mb: 1 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label={t('estadisticasNew.numberOfPeriods')}
                type="number"
                fullWidth
                value={unpublishedGame.number_of_periods}
                onChange={(e) => setUnpublishedGame({ ...unpublishedGame, number_of_periods: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label={t('estadisticasNew.periodDuration')}
                type="number"
                fullWidth
                value={unpublishedGame.period_duration}
                onChange={(e) => setUnpublishedGame({ ...unpublishedGame, period_duration: parseInt(e.target.value) })}
              />
            </Grid>
          </Grid>
          <TextField
            margin="dense"
            label={t('estadisticasNew.gameDate')}
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={unpublishedGame.game_date}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, game_date: e.target.value })}
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('estadisticasNew.location')}</InputLabel>
            <Select
              label={t('estadisticasNew.location')}
              value={unpublishedGame.game_location}
              onChange={(e) => setUnpublishedGame({ ...unpublishedGame, game_location: e.target.value })}
            >
              <MenuItem value="domicile">{t('estadisticasNew.local')}</MenuItem>
              <MenuItem value="exterieur">{t('estadisticasNew.away')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label={t('estadisticasNew.finalScore')}
            fullWidth
            value={unpublishedGame.score}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, score: e.target.value })}
            sx={{ mt: 2 }}
            placeholder={t('estadisticasNew.finalScorePlaceholder')}
            helperText={t('estadisticasNew.finalScoreHelper')}
          />

          <TextField
            margin="dense"
            label={t('estadisticasNew.gameNotes')}
            fullWidth
            multiline
            rows={3}
            value={unpublishedGame.notes}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, notes: e.target.value })}
            sx={{ mt: 2 }}
            placeholder={t('estadisticasNew.gameNotesPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenNewVideoDialog(false);
            setNewVideoUrl('');
            setUnpublishedGame({
              game_label: '',
              adversary_name: '',
              number_of_periods: 4,
              period_duration: 10,
              game_date: new Date().toISOString().split('T')[0],
              game_location: 'domicile',
              score: '',
              notes: ''
            });
          }}>
            {t('estadisticasNew.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (!newVideoUrl) {
                showSnackbar(t('estadisticasNew.provideVideoUrlWarning'), 'warning');
                return;
              }
              if (!unpublishedGame.game_label || !unpublishedGame.adversary_name) {
                showSnackbar(t('estadisticasNew.completeGameInfo'), 'warning');
                return;
              }

              // Set video URL and configure game
              setVideoUrl(newVideoUrl);
              setCurrentGame({
                ...unpublishedGame,
                isNewGame: true // Flag to create new game on save
              });

              // Initialize score from pre-entered value if available
              let initialLocal = 0;
              let initialVisitante = 0;
              if (unpublishedGame.score && unpublishedGame.score.includes('-')) {
                const scoreParts = unpublishedGame.score.split('-').map(s => s.trim());
                if (scoreParts.length === 2) {
                  initialLocal = parseInt(scoreParts[0]) || 0;
                  initialVisitante = parseInt(scoreParts[1]) || 0;
                }
              }

              // Reset states
              setPuntuacionLocal(initialLocal);
              setPuntuacionVisitante(initialVisitante);
              setCurrentPeriod(1);
              setActionsLog([]);
              setPlayerTimeTracking({});

              // Close dialogs and start video mode
              setOpenNewVideoDialog(false);

              // Start in video mode
              // No need to set stats mode again, we're already in VIDEO mode
            }}
            variant="contained"
            color="primary"
            disabled={!newVideoUrl || !unpublishedGame.game_label || !unpublishedGame.adversary_name}
          >
            {t('estadisticasNew.startStats')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Config Game Dialog (for live mode) */}
      <Dialog open={openConfigGameDialog} onClose={() => setOpenConfigGameDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('estadisticasNew.configLiveGame')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t('estadisticasNew.gameName')}
            fullWidth
            required
            value={unpublishedGame.game_label}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, game_label: e.target.value })}
          />
          <TextField
            margin="dense"
            label={t('estadisticasNew.adversaryName')}
            fullWidth
            required
            value={unpublishedGame.adversary_name}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, adversary_name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Número de períodos"
            type="number"
            fullWidth
            required
            value={unpublishedGame.number_of_periods}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, number_of_periods: parseInt(e.target.value) })}
          />
          <TextField
            margin="dense"
            label="Duración del período (minutos)"
            type="number"
            fullWidth
            required
            value={unpublishedGame.period_duration}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, period_duration: parseInt(e.target.value) })}
          />
          <TextField
            margin="dense"
            label={t('estadisticasNew.gameDate')}
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={unpublishedGame.game_date}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, game_date: e.target.value })}
          />
          <FormControl fullWidth sx={{ mt: 2 }} required>
            <InputLabel>{t('estadisticasNew.location')}</InputLabel>
            <Select
              label={t('estadisticasNew.location')}
              value={unpublishedGame.game_location}
              onChange={(e) => setUnpublishedGame({ ...unpublishedGame, game_location: e.target.value })}
            >
              <MenuItem value="domicile">{t('estadisticasNew.local')}</MenuItem>
              <MenuItem value="exterieur">{t('estadisticasNew.away')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label={t('estadisticasNew.gameNotes')}
            fullWidth
            multiline
            rows={3}
            value={unpublishedGame.notes}
            onChange={(e) => setUnpublishedGame({ ...unpublishedGame, notes: e.target.value })}
            placeholder={t('estadisticasNew.gameNotesPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenConfigGameDialog(false);
            resetComponent();
          }}>
            {t('estadisticasNew.cancel')}
          </Button>
          <Button
            onClick={handleConfigGame}
            variant="contained"
            color="primary"
            disabled={
              !unpublishedGame.game_label.trim() ||
              !unpublishedGame.adversary_name.trim() ||
              !unpublishedGame.number_of_periods ||
              !unpublishedGame.period_duration ||
              !unpublishedGame.game_date
            }
          >
            {t('estadisticasNew.startGame')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <Dialog
        open={openExitConfirmDialog}
        onClose={isSaving ? undefined : handleCancelExit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color={isSaving ? 'info' : 'warning'} />
          {isSaving ? t('estadisticasNew.saving') : t('estadisticasNew.confirmExitTitle')}
        </DialogTitle>
        <DialogContent>
          {isSaving ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography sx={{ mb: 2 }}>
                {t('estadisticasNew.savingData')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <div className="spinner" style={{
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #1976d2',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  animation: 'spin 1s linear infinite'
                }} />
              </Box>
            </Box>
          ) : !isOnline ? (
            <Box>
              <Typography color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                {t('estadisticasNew.noConnectionWarning')}
              </Typography>
              <Typography>
                {t('estadisticasNew.noConnectionMessage')}
              </Typography>
            </Box>
          ) : saveFailed ? (
            <Box>
              <Typography color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                {t('estadisticasNew.saveErrorTitle')}
              </Typography>
              <Typography>
                {t('estadisticasNew.saveErrorMessage')}
              </Typography>
            </Box>
          ) : (
            <Typography>
              {t('estadisticasNew.exitConfirmMessage')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {isSaving ? (
            <Typography variant="caption" sx={{ color: 'text.secondary', mr: 2 }}>
              {t('estadisticasNew.dontCloseWindow')}
            </Typography>
          ) : (
            <>
              <Button onClick={handleCancelExit} color="primary">
                {t('estadisticasNew.cancel')}
              </Button>
              {saveFailed && (
                <Button
                  onClick={() => {
                    setOpenExitConfirmDialog(false);
                    handleSaveGameAndActions();
                  }}
                  color="success"
                  variant="contained"
                >
                  {t('estadisticasNew.retrySave')}
                </Button>
              )}
              {!isOnline ? (
                <Button disabled color="error" variant="contained">
                  {t('estadisticasNew.noConnection')}
                </Button>
              ) : (
                <Button onClick={handleConfirmExit} color="error" variant="contained">
                  {t('estadisticasNew.exitWithoutSaving')}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Tags Dialog - Simplified for selective player selection */}
      <Dialog
        open={openTagsDialog}
        onClose={() => {
          setOpenTagsDialog(false);
          setSelectedTag(null);
          setSelectedPlayersForTag([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOfferIcon color="primary" />
          {t('estadisticasNew.applyTag', { tag: selectedTag?.libelle })}
        </DialogTitle>
        <DialogContent>
          {selectedTag && (
            <Box>
              {selectedTag.description && (
                <Box sx={{ bgcolor: 'info.main', color: 'white', p: 1, borderRadius: 1, mb: 2 }}>
                  <Typography variant="caption">
                    {selectedTag.description}
                  </Typography>
                </Box>
              )}

              {/* Players Selection */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {t('estadisticasNew.selectPlayersOnCourt')}
              </Typography>

              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  if (selectedPlayersForTag.length === activePlayers.length) {
                    setSelectedPlayersForTag([]);
                  } else {
                    setSelectedPlayersForTag(activePlayers.map(p => p.id));
                  }
                }}
                startIcon={<GroupIcon />}
                sx={{ mb: 2 }}
              >
                {selectedPlayersForTag.length === activePlayers.length
                  ? t('estadisticasNew.deselectAll')
                  : t('estadisticasNew.selectAllOnCourt')}
              </Button>

              {/* Active Players Only */}
              {activePlayers.length > 0 ? (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                    {t('estadisticasNew.playersOnCourt', { count: activePlayers.length })}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1 }}>
                    {activePlayers.map(player => (
                      <Card
                        key={player.id}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          bgcolor: selectedPlayersForTag.includes(player.id)
                            ? 'primary.main'
                            : 'background.paper',
                          color: selectedPlayersForTag.includes(player.id)
                            ? 'white'
                            : 'text.primary',
                          transition: 'all 0.2s',
                          border: '2px solid',
                          borderColor: 'success.main',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: 2
                          }
                        }}
                        onClick={() => togglePlayerForTag(player.id)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {selectedPlayersForTag.includes(player.id) ? (
                            <CheckBoxIcon fontSize="small" />
                          ) : (
                            <CheckBoxOutlineBlankIcon fontSize="small" />
                          )}
                          <Avatar
                            src={player.avatarUrl}
                            sx={{
                              width: 30,
                              height: 30,
                              border: selectedPlayersForTag.includes(player.id)
                                ? '2px solid white'
                                : '2px solid transparent'
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {player.nom}
                          </Typography>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                  {t('estadisticasNew.noActivePlayers')}
                </Typography>
              )}


              {/* Summary */}
              {selectedTag && selectedPlayersForTag.length > 0 && (
                <Box sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'success.light',
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'success.main'
                }}>
                  <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 'bold' }}>
                    {t('estadisticasNew.tagWillBeApplied', { tag: selectedTag.libelle, count: selectedPlayersForTag.length })}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenTagsDialog(false);
            setSelectedTag(null);
            setSelectedPlayersForTag([]);
          }}>
            {t('estadisticasNew.cancel')}
          </Button>
          <Button
            onClick={handleApplyTag}
            variant="contained"
            color="primary"
            disabled={!selectedTag || selectedPlayersForTag.length === 0}
            startIcon={<CheckCircleIcon />}
          >
            {t('estadisticasNew.applyTagButton')}
          </Button>
        </DialogActions>
      </Dialog>

    </DndProvider>
  );
}